// OPDConsultationTab.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { receptionAPI, departmentAPI, labAPI } from "../../services/api";
import { notify, confirmDialog } from '../../utils/notify';


const DRUG_FORMS = [
  "Tablet","Capsule","Syrup","Suspension","Injection",
  "Cream","Ointment","Gel","Drops","Inhaler",
  "Patch","Suppository","Powder","Other",
];
const DRUG_ROUTES = [
  "Oral","Sublingual","IV (Intravenous)","IM (Intramuscular)",
  "SC (Subcutaneous)","Topical","Inhalation","Rectal",
  "Nasal","Ophthalmic","Otic","Other",
];
const DRUG_FREQUENCIES = [
  "OD – Once daily","BD – Twice daily","TDS – Three times daily",
  "QID – Four times daily","1-0-1","1-1-1","1-0-0","0-0-1",
  "Every 6 hours","Every 8 hours","Every 12 hours",
  "At bedtime (HS)","Before meals (AC)","After meals (PC)",
  "With meals","SOS / As needed","Other",
];

const ASSOCIATED_SYMPTOMS = [
  "Fever","Headache","Cough","Shortness of breath","Chest pain",
  "Palpitations","Nausea","Vomiting","Diarrhea","Constipation",
  "Abdominal pain","Loss of appetite","Weight loss","Weight gain",
  "Fatigue / Weakness","Dizziness","Syncope (Fainting)","Swelling (Edema)",
  "Rash / Skin changes","Itching","Joint pain","Muscle pain",
  "Back pain","Dysuria (Painful urination)","Hematuria (Blood in urine)",
  "Polyuria (Frequent urination)","Polydipsia (Excessive thirst)",
  "Blurred vision","Hearing loss","Tremors","Numbness / Tingling",
  "Memory loss / Confusion",
];

const ICD10_LIST = [
  { code:"J06.9", desc:"Acute upper respiratory infection, unspecified" },
  { code:"J18.9", desc:"Pneumonia, unspecified" },
  { code:"J00",   desc:"Acute nasopharyngitis (Common cold)" },
  { code:"J11.1", desc:"Influenza with other respiratory manifestations" },
  { code:"J20.9", desc:"Acute bronchitis, unspecified" },
  { code:"J45.9", desc:"Asthma, unspecified" },
  { code:"J44.1", desc:"COPD with acute exacerbation" },
  { code:"K29.7", desc:"Gastritis, unspecified" },
  { code:"K21.0", desc:"GERD with esophagitis" },
  { code:"K59.0", desc:"Constipation" },
  { code:"K52.9", desc:"Noninfective gastroenteritis and colitis" },
  { code:"A09",   desc:"Other gastroenteritis and colitis (infective)" },
  { code:"I10",   desc:"Essential (primary) hypertension" },
  { code:"I25.1", desc:"Atherosclerotic heart disease" },
  { code:"I50.9", desc:"Heart failure, unspecified" },
  { code:"I48.9", desc:"Atrial fibrillation, unspecified" },
  { code:"E11.9", desc:"Type 2 diabetes mellitus without complications" },
  { code:"E10.9", desc:"Type 1 diabetes mellitus without complications" },
  { code:"E11.65",desc:"Type 2 diabetes mellitus with hyperglycemia" },
  { code:"E78.5", desc:"Hyperlipidemia, unspecified" },
  { code:"E03.9", desc:"Hypothyroidism, unspecified" },
  { code:"E05.90",desc:"Thyrotoxicosis, unspecified" },
  { code:"E66.9", desc:"Obesity, unspecified" },
  { code:"N39.0", desc:"Urinary tract infection, site not specified" },
  { code:"N18.9", desc:"Chronic kidney disease, unspecified" },
  { code:"N20.0", desc:"Calculus of kidney (Renal stone)" },
  { code:"M54.5", desc:"Low back pain" },
  { code:"M25.5", desc:"Pain in joint" },
  { code:"M10.9", desc:"Gout, unspecified" },
  { code:"G43.9", desc:"Migraine, unspecified" },
  { code:"G40.9", desc:"Epilepsy, unspecified" },
  { code:"F32.9", desc:"Major depressive disorder, single episode" },
  { code:"F41.1", desc:"Generalized anxiety disorder" },
  { code:"L50.9", desc:"Urticaria, unspecified" },
  { code:"L20.9", desc:"Atopic dermatitis, unspecified" },
  { code:"A01.0", desc:"Typhoid fever" },
  { code:"A90",   desc:"Dengue fever" },
  { code:"B50.9", desc:"Plasmodium falciparum malaria, unspecified" },
  { code:"Z00.00",desc:"General examination without complaint" },
  { code:"Z23",   desc:"Encounter for immunization" },
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const calcBMI = (weightKg, heightCm) => {
  const w = parseFloat(weightKg), h = parseFloat(heightCm);
  if (!w || !h || h === 0) return null;
  return (w / Math.pow(h / 100, 2)).toFixed(1);
};
const getBMICategory = (bmi) => {
  const v = parseFloat(bmi);
  if (isNaN(v))  return { label: "", color: "" };
  if (v < 18.5)  return { label: "Underweight", color: "#d97706" };
  if (v < 25)    return { label: "Normal",      color: "#16a34a" };
  if (v < 30)    return { label: "Overweight",  color: "#ea580c" };
  return               { label: "Obese",        color: "#dc2626" };
};
const formatDate = (d) => {
  try { return d ? new Date(d).toLocaleDateString("en-IN",{ day:"2-digit",month:"short",year:"numeric" }) : "—"; }
  catch { return d || "—"; }
};
const formatTime = (t) => {
  try { return t ? new Date(`1970-01-01T${t}`).toLocaleTimeString("en-IN",{ hour:"2-digit",minute:"2-digit" }) : ""; }
  catch { return t || ""; }
};
const formatDateTime = (d) => {
  try { return d ? new Date(d).toLocaleString("en-IN",{ day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit" }) : "—"; }
  catch { return d || "—"; }
};

// Extract OPD record from token — handles all backend serializer shapes
const extractRecord = (token) =>
  token?.record_data || token?.opd_record || token?.record || null;

const getPatientName = (token) => {
  const rec = extractRecord(token);
  return rec?.patient_name_snapshot
    || token?.patient_name
    || token?.patient?.name
    || token?.patient?.full_name
    || null;
};
const getPatientAge    = (t) => { const r = extractRecord(t); return r?.patient_age    || t?.patient_age    || null; };
const getPatientGender = (t) => { const r = extractRecord(t); return r?.patient_gender || t?.patient_gender || null; };
const getPatientId     = (t) => { const r = extractRecord(t); return r?.patient        || t?.patient?.id    || t?.patient || null; };

// =============================================================================
// DESIGN TOKENS
// =============================================================================

const COLOR = {
  primary:   "#2563eb",
  success:   "#16a34a",
  warning:   "#d97706",
  danger:    "#dc2626",
  gray:      "#6b7280",
  lightGray: "#9ca3af",
  border:    "#e5e7eb",
  bg:        "#f8fafc",
  bgSection: "#f9fafb",
  text:      "#111827",
  textSub:   "#374151",
  assigned:  "#0ea5e9",   // highlight for currently-assigned doctor
};

const styles = {
  overlay:{ position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:1000,padding:"20px 12px",overflowY:"auto" },
  modal:{ background:"#fff",borderRadius:12,width:"100%",maxWidth:920,boxShadow:"0 20px 60px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 40px)",overflow:"hidden" },
  modalLarge:{ background:"#fff",borderRadius:12,width:"100%",maxWidth:1080,boxShadow:"0 20px 60px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 40px)",overflow:"hidden" },
  modalHeader:{ padding:"18px 24px",borderBottom:`1px solid ${COLOR.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:COLOR.bg,borderRadius:"12px 12px 0 0",flexShrink:0 },
  modalBody:{ padding:"22px 24px",overflowY:"auto",flex:1 },
  modalFooter:{ padding:"14px 24px",borderTop:`1px solid ${COLOR.border}`,display:"flex",gap:10,justifyContent:"flex-end",background:COLOR.bg,borderRadius:"0 0 12px 12px",flexShrink:0 },
  heading:{ fontSize:18,fontWeight:800,color:COLOR.text,margin:0 },
  sectionTitle:{ fontSize:13,fontWeight:700,color:COLOR.textSub,margin:"0 0 10px 0" },
  label:{ fontSize:12,fontWeight:600,color:COLOR.textSub,marginBottom:4,display:"block" },
  small:{ fontSize:12,color:COLOR.gray },
  input:{ width:"100%",padding:"8px 12px",border:`1px solid ${COLOR.border}`,borderRadius:7,fontSize:13,color:COLOR.text,background:"#fff",outline:"none",boxSizing:"border-box",transition:"border-color 0.15s" },
  textarea:{ width:"100%",padding:"8px 12px",border:`1px solid ${COLOR.border}`,borderRadius:7,fontSize:13,color:COLOR.text,background:"#fff",outline:"none",resize:"vertical",boxSizing:"border-box",minHeight:76 },
  select:{ width:"100%",padding:"8px 12px",border:`1px solid ${COLOR.border}`,borderRadius:7,fontSize:13,color:COLOR.text,background:"#fff",outline:"none",boxSizing:"border-box" },
  section:{ background:COLOR.bgSection,border:`1px solid ${COLOR.border}`,borderRadius:10,padding:"16px 18px",marginBottom:16 },
  grid2:{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 },
  grid3:{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12 },
  grid4:{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10 },
  btnPrimary:{ padding:"9px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:COLOR.primary,color:"#fff",display:"flex",alignItems:"center",gap:6 },
  btnSuccess:{ padding:"9px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:COLOR.success,color:"#fff",display:"flex",alignItems:"center",gap:6 },
  btnNeutral:{ padding:"9px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:"#64748b",color:"#fff",display:"flex",alignItems:"center",gap:6 },
  btnOutline:{ padding:"9px 20px",borderRadius:8,border:`1px solid ${COLOR.border}`,cursor:"pointer",fontSize:13,fontWeight:600,background:"#fff",color:COLOR.textSub,display:"flex",alignItems:"center",gap:6 },
  btnDanger:{ padding:"9px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:COLOR.danger,color:"#fff",display:"flex",alignItems:"center",gap:6 },
  alertWarning:{ background:"#fef3c7",border:"1px solid #f59e0b",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#92400e",marginBottom:12 },
  alertError:{ background:"#fee2e2",border:"1px solid #ef4444",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#991b1b",marginBottom:12 },
  alertSuccess:{ background:"#dcfce7",border:"1px solid #86efac",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#166534",marginBottom:12 },
  alertInfo:{ background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#1e40af",marginBottom:12 },
  card:{ background:"#fff",border:`1px solid ${COLOR.border}`,borderRadius:9,padding:"14px 16px",marginBottom:10 },
};

// =============================================================================
// SMALL REUSABLE COMPONENTS
// =============================================================================

const CloseButton = ({ onClick }) => (
  <button onClick={onClick} style={{ background:"none",border:"none",cursor:"pointer",fontSize:24,color:COLOR.gray,lineHeight:1,padding:"2px 6px" }} aria-label="Close">×</button>
);

const LoadingSpinner = ({ size=20, color=COLOR.primary }) => (
  <span style={{ display:"inline-block",width:size,height:size,border:"3px solid #e5e7eb",borderTopColor:color,borderRadius:"50%",animation:"opd-spin 0.7s linear infinite",flexShrink:0 }} />
);

const StatusBadge = ({ status }) => {
  const config = {
    COMPLETED:   { bg:"#dcfce7",color:"#166534" },
    IN_PROGRESS: { bg:"#fef9c3",color:"#854d0e" },
    WAITING:     { bg:"#dbeafe",color:"#1e40af" },
    CANCELLED:   { bg:"#fee2e2",color:"#991b1b" },
  }[status] || { bg:"#f3f4f6",color:"#374151" };
  return (
    <span style={{ display:"inline-flex",alignItems:"center",padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700,color:config.color,background:config.bg }}>{status}</span>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const config = {
    routine: { bg:"#dcfce7",color:"#166534" },
    urgent:  { bg:"#fef9c3",color:"#854d0e" },
    stat:    { bg:"#fee2e2",color:"#991b1b" },
  }[urgency?.toLowerCase()] || { bg:"#f3f4f6",color:"#374151" };
  return (
    <span style={{ padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:700,color:config.color,background:config.bg }}>{urgency?.toUpperCase()}</span>
  );
};

// ─── NEW: Contributor badge shown per consultation tab ───────────────────────
const ContributorBadge = ({ data, sectionLabel }) => {
  if (!data?.doctor_name) return null;
  const timeStr = data.at
    ? new Date(data.at).toLocaleString('en-IN', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit',
      })
    : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: 7,
      padding: '7px 12px',
      marginBottom: 12,
      fontSize: 12,
    }}>
      <span style={{ fontSize: 16 }}>✏️</span>
      <div>
        <span style={{ fontWeight: 700, color: '#0369a1' }}>{data.doctor_name}</span>
        <span style={{ color: '#0284c7' }}> filled this section</span>
        {timeStr && (
          <span style={{ color: '#64748b', marginLeft: 6 }}>· {timeStr}</span>
        )}
      </div>
      {/* Visual indicator that this is a contribution, not from assigned doctor */}
      <span style={{
        marginLeft: 'auto', fontSize: 10, fontWeight: 700,
        background: '#e0f2fe', color: '#0369a1',
        padding: '2px 7px', borderRadius: 4,
        textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        Contributed
      </span>
    </div>
  );
};

// ─── NEW: Single doctor option card used in referral picker ──────────────────
const DoctorOptionCard = ({ doctor, selected, onSelect }) => (
  <div
    onClick={onSelect}
    style={{
      padding:"10px 14px",borderRadius:8,cursor:"pointer",marginBottom:6,
      border:`1px solid ${selected ? COLOR.primary : COLOR.border}`,
      background:selected ? "#eff6ff" : "#fff",
      transition:"all 0.15s",
    }}
  >
    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
      <div>
        <div style={{ fontWeight:700,fontSize:13,color:selected ? COLOR.primary : COLOR.text }}>
          Dr. {doctor.full_name || `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim()}
          {selected && <span style={{ marginLeft:8,fontSize:11,color:COLOR.primary }}>✓ Selected</span>}
        </div>
        {(doctor.designation || doctor.qualification) && (
          <div style={{ fontSize:11,color:COLOR.gray,marginTop:2 }}>
            {[doctor.qualification, doctor.designation].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>
      <div style={{ textAlign:"right" }}>
        {doctor.availability_status && (
          <span style={{
            fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:4,
            background:doctor.availability_status === "AVAILABLE" ? "#dcfce7" : "#fef9c3",
            color:doctor.availability_status === "AVAILABLE" ? "#166534" : "#854d0e",
          }}>
            {doctor.availability_status}
          </span>
        )}
        {typeof doctor.current_queue_count === "number" && (
          <div style={{ fontSize:11,color:COLOR.gray,marginTop:2 }}>Queue: {doctor.current_queue_count}</div>
        )}
      </div>
    </div>
  </div>
);

// =============================================================================
// VITALS DISPLAY (read-only compact)
// =============================================================================

const VitalsDisplay = ({ vitals }) => {
  if (!vitals || Object.keys(vitals).length === 0) return null;
  const bmi = vitals.bmi || calcBMI(vitals.weight_kg, vitals.height_cm);
  const cat = getBMICategory(bmi);
  const chips = [
    vitals.bp_sys && vitals.bp_dia ? { label:"BP",    value:`${vitals.bp_sys}/${vitals.bp_dia} mmHg` } : null,
    vitals.pulse                   ? { label:"Pulse", value:`${vitals.pulse} bpm` }                    : null,
    vitals.temp                    ? { label:"Temp",  value:`${vitals.temp}°${vitals.temp_unit || "F"}` } : null,
    vitals.spo2                    ? { label:"SpO₂",  value:`${vitals.spo2}%` }                         : null,
    vitals.rr                      ? { label:"RR",    value:`${vitals.rr}/min` }                         : null,
    vitals.weight_kg               ? { label:"Wt",    value:`${vitals.weight_kg} kg` }                   : null,
    vitals.height_cm               ? { label:"Ht",    value:`${vitals.height_cm} cm` }                   : null,
    bmi                            ? { label:"BMI",   value:`${bmi} (${cat.label})`, color:cat.color }   : null,
  ].filter(Boolean);
  return (
    <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
      {chips.map((chip) => (
        <span key={chip.label} style={{ background:"#f1f5f9",borderRadius:6,padding:"3px 10px",fontSize:12,display:"flex",gap:4,alignItems:"center" }}>
          <span style={{ color:COLOR.gray,fontWeight:500 }}>{chip.label}:</span>
          <span style={{ color:chip.color || COLOR.text,fontWeight:700 }}>{chip.value}</span>
        </span>
      ))}
    </div>
  );
};

// =============================================================================
// PRESCRIPTION DISPLAY (read-only list)
// =============================================================================

const PrescriptionDisplay = ({ prescription }) => {
  if (!prescription?.length) return <span style={styles.small}>None prescribed</span>;
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
      {prescription.map((med, i) => (
        <div key={i} style={{ background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"8px 12px",fontSize:13 }}>
          <div style={{ fontWeight:700,color:"#166534" }}>
            {i+1}. {med.name}
            {med.generic && <span style={{ fontWeight:400,color:COLOR.gray }}> ({med.generic})</span>}
            {med.dosage  && <span style={{ fontWeight:400,color:COLOR.textSub }}> — {med.dosage}</span>}
            {med.form    && <span style={{ fontWeight:400,color:COLOR.gray }}> · {med.form}</span>}
          </div>
          <div style={{ color:COLOR.textSub,marginTop:2,fontSize:12 }}>
            {[med.route, med.frequency, med.duration].filter(Boolean).join(" · ")}
            {med.quantity && <span style={{ color:COLOR.gray }}> · Qty: {med.quantity}</span>}
          </div>
          {med.instructions && <div style={{ color:COLOR.gray,fontStyle:"italic",fontSize:12,marginTop:2 }}>{med.instructions}</div>}
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// INVESTIGATIONS DISPLAY (read-only)
// =============================================================================

const InvestigationsDisplay = ({ investigations }) => {
  if (!investigations?.length) return <span style={styles.small}>None ordered</span>;
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
      {investigations.map((inv, i) => (
        <div key={i} style={{ background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",fontSize:13 }}>
          <div style={{ flex:1 }}>
            <span style={{ fontWeight:700,color:"#1e40af" }}>{inv.name}</span>
            {inv.category && <span style={{ color:COLOR.gray,fontSize:12 }}> · {inv.category}</span>}
            {inv.instructions && <div style={{ color:COLOR.textSub,fontSize:12,marginTop:2 }}>{inv.instructions}</div>}
          </div>
          <div style={{ display:"flex",gap:6,alignItems:"center",flexShrink:0,marginLeft:8 }}>
            {inv.urgency && <UrgencyBadge urgency={inv.urgency} />}
            {inv.status && <span style={{ padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600,background:"#f3f4f6",color:COLOR.gray }}>{inv.status.replace(/_/g," ")}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// VISIT CARD (collapsible — used in history modal)
// =============================================================================

const VisitCard = ({ visit, defaultOpen=false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border:`1px solid ${COLOR.border}`,borderRadius:10,overflow:"hidden",marginBottom:12 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 18px",cursor:"pointer",background:open ? "#f0f9ff" : COLOR.bg,borderBottom:open ? `1px solid ${COLOR.border}` : "none",transition:"background 0.15s" }}>
        <div>
          <div style={{ fontWeight:700,fontSize:14,color:COLOR.text }}>
            {formatDate(visit.visit_date)}
            {visit.visit_time && <span style={{ fontWeight:400,color:COLOR.gray,fontSize:12,marginLeft:8 }}>{formatTime(visit.visit_time)}</span>}
          </div>
          <div style={{ fontSize:12,color:COLOR.gray,marginTop:2 }}>
            {visit.doctor_name && `${visit.doctor_name}`}
            {visit.assigned_doctor_name && visit.assigned_doctor_name !== visit.doctor_name && (
              <span style={{ color:COLOR.assigned }}> → {visit.assigned_doctor_name}</span>
            )}
            {visit.department_name && ` · ${visit.department_name}`}
            {visit.token_number && ` · Token #${visit.token_number}`}
          </div>
          {visit.diagnosis && (
            <div style={{ fontSize:12,color:COLOR.textSub,fontStyle:"italic",marginTop:2 }}>
              {visit.diagnosis.length > 70 ? `${visit.diagnosis.slice(0,70)}…` : visit.diagnosis}
            </div>
          )}
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <StatusBadge status={visit.status} />
          <span style={{ color:COLOR.lightGray,fontSize:16 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && (
        <div style={{ padding:"16px 20px" }}>
          {visit.chief_complaint && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:4 }}>CHIEF COMPLAINT</div>
              <div style={{ fontSize:13 }}>{visit.chief_complaint}</div>
            </div>
          )}
          {visit.vitals && Object.keys(visit.vitals).length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>VITALS</div>
              <VitalsDisplay vitals={visit.vitals} />
            </div>
          )}
          {visit.diagnosis && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:4 }}>DIAGNOSIS</div>
              <div style={{ fontSize:13,fontWeight:600 }}>{visit.diagnosis}</div>
              {visit.diagnosis_detail?.icd10_codes?.length > 0 && (
                <div style={{ display:"flex",flexWrap:"wrap",gap:4,marginTop:6 }}>
                  {visit.diagnosis_detail.icd10_codes.map((c) => (
                    <span key={c.code} style={{ background:"#dbeafe",color:"#1e40af",borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:600 }}>
                      {c.code} — {c.desc.slice(0,35)}{c.desc.length > 35 ? "…" : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {visit.prescription?.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>PRESCRIPTION</div>
              <PrescriptionDisplay prescription={visit.prescription} />
            </div>
          )}
          {visit.investigations_detail?.length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>INVESTIGATIONS</div>
              <InvestigationsDisplay investigations={visit.investigations_detail} />
            </div>
          )}
          {visit.referral?.referred_to && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:4 }}>REFERRAL</div>
              <div style={{ background:"#fef3c7",border:"1px solid #fcd34d",borderRadius:7,padding:"8px 12px",fontSize:12 }}>
                → <strong>{visit.referral.referred_to}</strong>
                {visit.referral.type && <span style={{ color:COLOR.gray }}> ({visit.referral.type})</span>}
                {visit.referral.urgency && <UrgencyBadge urgency={visit.referral.urgency} />}
                {visit.referral.reason && <div style={{ marginTop:4,color:COLOR.textSub }}>{visit.referral.reason}</div>}
              </div>
            </div>
          )}
          {/* Contributors summary */}
          {visit.contributors && Object.keys(visit.contributors).length > 0 && (
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>CONTRIBUTORS</div>
              <div style={{ display:"flex",flexWrap:"wrap",gap:6 }}>
                {Object.entries(visit.contributors).map(([section, info]) => (
                  <span key={section} style={{ background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:6,padding:"3px 8px",fontSize:11,color:"#1e40af" }}>
                    <strong style={{ textTransform:"capitalize" }}>{section}</strong>: {info.doctor_name}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div style={{ borderTop:`1px solid #f3f4f6`,paddingTop:10,marginTop:8,display:"flex",gap:20,flexWrap:"wrap" }}>
            {visit.consultation_started_at && <span style={styles.small}>Started: {formatDateTime(visit.consultation_started_at)}</span>}
            {visit.consultation_completed_at && <span style={styles.small}>Completed: {formatDateTime(visit.consultation_completed_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PREVIOUS DETAILS MODAL
// =============================================================================

const PreviousDetailsModal = ({ patientId, patientName, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [search,  setSearch]  = useState("");

  useEffect(() => {
    if (!patientId) {
      setError("Patient ID not available.");
      setLoading(false);
      return;
    }
    receptionAPI.getPatientOPDHistory(patientId)
      .then((data) => setHistory(Array.isArray(data) ? data : data.results || []))
      .catch(() => setError("Failed to load visit history."))
      .finally(() => setLoading(false));
  }, [patientId]);

  const filtered = history.filter((v) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (v.diagnosis || "").toLowerCase().includes(q) ||
      (v.chief_complaint || "").toLowerCase().includes(q) ||
      (v.doctor_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={styles.overlay}>
      <div style={styles.modalLarge}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.heading}>Visit History — {patientName || "Patient"}</div>
            <div style={{ fontSize:12,color:COLOR.gray,marginTop:3 }}>{history.length} visit{history.length !== 1 ? "s" : ""} on record</div>
          </div>
          <CloseButton onClick={onClose} />
        </div>
        <div style={{ padding:"14px 24px",borderBottom:`1px solid ${COLOR.border}`,flexShrink:0 }}>
          <input style={styles.input} placeholder="Search by diagnosis, chief complaint, or doctor…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={styles.modalBody}>
          {loading && <div style={{ textAlign:"center",padding:56 }}><LoadingSpinner size={32} /><div style={{ marginTop:12,color:COLOR.gray }}>Loading visit history…</div></div>}
          {error && <div style={styles.alertError}>{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div style={{ textAlign:"center",padding:56,color:COLOR.lightGray }}>{search ? "No visits match your search." : "No previous visits found."}</div>
          )}
          {filtered.map((visit, i) => <VisitCard key={visit.id || i} visit={visit} defaultOpen={i === 0 && !search} />)}
        </div>
        <div style={styles.modalFooter}>
          <button style={styles.btnNeutral} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MEDICATION ROW
// =============================================================================

const emptyMedication = () => ({ name:"",generic:"",dosage:"",form:"Tablet",route:"Oral",frequency:"BD – Twice daily",duration:"",quantity:"",instructions:"" });

const MedicationRow = ({ med, index, onChange, onRemove }) => {
  const update = (field) => (e) => onChange(index, { ...med, [field]: e.target.value });
  return (
    <div style={{ border:`1px solid ${COLOR.border}`,borderRadius:9,padding:"14px 16px",marginBottom:10,background:"#fff" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10 }}>
        <span style={{ fontWeight:700,fontSize:13,color:COLOR.textSub }}>Drug #{index+1}</span>
        <button onClick={() => onRemove(index)} style={{ background:"none",border:"none",cursor:"pointer",color:COLOR.danger,fontSize:20,lineHeight:1,padding:2 }}>×</button>
      </div>
      <div style={{ ...styles.grid3,marginBottom:10 }}>
        <div><label style={styles.label}>Drug Name *</label><input style={styles.input} value={med.name} onChange={update("name")} placeholder="e.g. Amoxicillin" /></div>
        <div><label style={styles.label}>Generic Name</label><input style={styles.input} value={med.generic} onChange={update("generic")} placeholder="e.g. Amoxicillin Trihydrate" /></div>
        <div><label style={styles.label}>Dosage / Strength</label><input style={styles.input} value={med.dosage} onChange={update("dosage")} placeholder="e.g. 500 mg" /></div>
      </div>
      <div style={{ ...styles.grid4,marginBottom:10 }}>
        <div><label style={styles.label}>Form</label><select style={styles.select} value={med.form} onChange={update("form")}>{DRUG_FORMS.map(f=><option key={f} value={f}>{f}</option>)}</select></div>
        <div><label style={styles.label}>Route</label><select style={styles.select} value={med.route} onChange={update("route")}>{DRUG_ROUTES.map(r=><option key={r} value={r}>{r}</option>)}</select></div>
        <div><label style={styles.label}>Frequency</label><select style={styles.select} value={med.frequency} onChange={update("frequency")}>{DRUG_FREQUENCIES.map(f=><option key={f} value={f}>{f}</option>)}</select></div>
        <div><label style={styles.label}>Duration</label><input style={styles.input} value={med.duration} onChange={update("duration")} placeholder="e.g. 5 days" /></div>
      </div>
      <div style={styles.grid2}>
        <div><label style={styles.label}>Qty to Dispense</label><input style={styles.input} value={med.quantity} onChange={update("quantity")} placeholder="e.g. 10 tablets" /></div>
        <div><label style={styles.label}>Special Instructions</label><input style={styles.input} value={med.instructions} onChange={update("instructions")} placeholder="e.g. Take after meals" /></div>
      </div>
    </div>
  );
};

// =============================================================================
// INVESTIGATION ROW
// =============================================================================

const emptyInvestigation = () => ({ name:"",category:"Laboratory",instructions:"",urgency:"routine",status:"ordered" });

// =============================================================================
// ICD-10 CODE PICKER
// =============================================================================

const ICD10Picker = ({ selected, onAdd, onRemove }) => {
  const [query, setQuery] = useState("");
  const results = query.trim().length >= 2
    ? ICD10_LIST.filter(c => c.code.toLowerCase().includes(query.toLowerCase()) || c.desc.toLowerCase().includes(query.toLowerCase())).slice(0,10)
    : [];
  const handleAdd = (code) => { if (!selected.find(s => s.code === code.code)) onAdd(code); setQuery(""); };
  return (
    <div>
      <div style={{ position:"relative" }}>
        <input style={styles.input} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search ICD-10 code or description…" />
        {results.length > 0 && (
          <div style={{ position:"absolute",top:"100%",left:0,right:0,zIndex:100,background:"#fff",border:`1px solid ${COLOR.border}`,borderRadius:8,boxShadow:"0 6px 24px rgba(0,0,0,0.12)",maxHeight:220,overflowY:"auto" }}>
            {results.map(c => (
              <div key={c.code} onClick={() => handleAdd(c)} style={{ padding:"9px 14px",cursor:"pointer",fontSize:13,borderBottom:`1px solid #f3f4f6`,display:"flex",gap:10,alignItems:"center" }}
                onMouseEnter={e => { e.currentTarget.style.background="#f0f9ff"; }} onMouseLeave={e => { e.currentTarget.style.background="#fff"; }}>
                <span style={{ fontWeight:700,color:COLOR.primary,minWidth:52,flexShrink:0 }}>{c.code}</span>
                <span style={{ color:COLOR.textSub }}>{c.desc}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {selected.length > 0 && (
        <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginTop:10 }}>
          {selected.map(c => (
            <span key={c.code} style={{ background:"#dbeafe",color:"#1e40af",borderRadius:6,padding:"4px 10px",fontSize:12,display:"flex",alignItems:"center",gap:6 }}>
              <strong>{c.code}</strong>
              <span style={{ maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{c.desc}</span>
              <button onClick={() => onRemove(c.code)} style={{ background:"none",border:"none",cursor:"pointer",color:"#1e40af",fontSize:15,lineHeight:1,padding:0 }}>×</button>
            </span>
          ))}
        </div>
      )}
      {selected.length === 0 && <div style={{ ...styles.small,marginTop:6 }}>Type at least 2 characters to search ICD-10 codes.</div>}
    </div>
  );
};

// =============================================================================
// CONSULTATION MODAL  (9-tab clinical form)
// =============================================================================

const CONSULTATION_TABS = [
  { id:"vitals",       label:"Vitals"        },
  { id:"symptoms",     label:"History"       },
  { id:"examination",  label:"Examination"   },
  { id:"diagnosis",    label:"Diagnosis"     },
  { id:"rx",           label:"Rx"            },
  { id:"advice",       label:"Advice"        },
  { id:"referral",     label:"Referral"      },
  { id:"notes",        label:"Notes"         },
  { id:"inv",          label:"Test Order"    },
  { id:"test-results", label:"Test Results"  },
];

const TestOrderingPanel = ({ recordId, patientName }) => {
  const [allTests,        setAllTests]        = useState([]);   // from API
  const [categories,      setCategories]      = useState([]);   // derived from API
  const [existingOrders,  setExistingOrders]  = useState([]);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [urgency,         setUrgency]         = useState('ROUTINE');
  const [instructions,    setInstructions]    = useState('');
  const [searchQ,         setSearchQ]         = useState('');
  const [activeCategory,  setActiveCategory]  = useState('');
  const [loading,         setLoading]         = useState(false);
  const [submitting,      setSubmitting]      = useState(false);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState('');

  // ── Fetch tests + existing orders ──────────────────────────────────────────
  useEffect(() => {
    if (!recordId) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [tests, orders] = await Promise.all([
          labAPI.getTests(),
          labAPI.getOrders({ opd_record_id: recordId }).catch(() => []),
        ]);
        const testList = Array.isArray(tests) ? tests : [];
        setAllTests(testList);
        setExistingOrders(Array.isArray(orders) ? orders : []);

        // ── Derive unique categories preserving backend order ─────────────────────
        const seen = new Set();
        const cats = [];
        testList.forEach(t => {
          const cat = t.category_name;
          // Skip null, blank, or "Uncategorised" — these are DB data issues
          if (!cat || cat === 'Uncategorised') return;
          if (!seen.has(cat)) { seen.add(cat); cats.push(cat); }
        });
        setCategories(cats);
        if (cats.length > 0) setActiveCategory(cats[0]);
      } catch (e) {
        setError('Failed to load lab tests. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [recordId]);

  // ── Tests visible in the active category tab ──────────────────────────────
  const testsInCategory = allTests.filter(t =>
      t.category_name && t.category_name !== 'Uncategorised' &&
      t.category_name === activeCategory
    );

  // Apply search (searches across ALL categories when searchQ is set)
  const searchResults = searchQ.trim().length >= 2
    ? allTests.filter(t =>
        t.name.toLowerCase().includes(searchQ.toLowerCase()) ||
        t.code.toLowerCase().includes(searchQ.toLowerCase())
      )
    : null;   // null = not in search mode

  const displayList = searchResults ?? testsInCategory;
  const isSearchMode = searchResults !== null;

  // ── Toggle selection ──────────────────────────────────────────────────────
  const toggle = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  // ── Derived totals ────────────────────────────────────────────────────────
  const selectedTests = allTests.filter(t => selectedIds.includes(t.id));
  const selectedTotal = selectedTests.reduce((s, t) => s + parseFloat(t.price || 0), 0);

  // ── Place order ───────────────────────────────────────────────────────────
  const handleOrder = async () => {
    if (selectedIds.length === 0) { setError('Select at least one test.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const order = await labAPI.createOrder({
        opd_record_id:       recordId,
        test_ids:            selectedIds,
        urgency,
        special_instructions: instructions,
      });
      setExistingOrders(prev => [order, ...prev]);
      setSelectedIds([]);
      setInstructions('');
      setUrgency('ROUTINE');
      setSuccess('✓ Lab order placed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const STATUS_LABEL = {
    ORDERED: 'Ordered', BILLED: 'Billed', PAID: 'Paid',
    SAMPLE_COLLECTED: 'Sample Collected', IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed', CANCELLED: 'Cancelled',
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
      <LoadingSpinner size={28} />
      <div style={{ marginTop: 12, fontSize: 13 }}>Loading registered tests…</div>
    </div>
  );

  // ── Empty state (no tests in backend yet) ─────────────────────────────────
  if (!loading && allTests.length === 0 && !error) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>🧪</div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>No lab tests registered yet</div>
      <div style={{ fontSize: 12 }}>Ask an admin to run: <code>python manage.py seed_lab_tests</code></div>
    </div>
  );

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 16, minHeight: 420 }}>

      {/* ── LEFT: category tabs + test list ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Error banner */}
        {error && (
          <div style={{ background: '#fee2e2', color: '#dc2626', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Search bar — searches across all categories */}
        <input
          style={{
            padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
            fontSize: 13, outline: 'none', boxSizing: 'border-box',
          }}
          placeholder="Search all tests by name or code…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
        />

        {/* Category tabs — hidden when searching */}
        {!isSearchMode && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {categories.map(cat => {
              const countSelected = allTests.filter(
                t => (t.category_name || 'Other') === cat && selectedIds.includes(t.id)
              ).length;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', border: 'none', transition: 'all .12s',
                    background: activeCategory === cat ? '#0d9488' : '#f1f5f9',
                    color: activeCategory === cat ? '#fff' : '#475569',
                    boxShadow: activeCategory === cat ? '0 1px 4px rgba(13,148,136,.3)' : 'none',
                    position: 'relative',
                  }}
                >
                  {cat}
                  {countSelected > 0 && activeCategory !== cat && (
                    <span style={{
                      position: 'absolute', top: -4, right: -4,
                      background: '#f97316', color: '#fff',
                      borderRadius: '50%', width: 16, height: 16,
                      fontSize: 9, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {countSelected}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Search mode label */}
        {isSearchMode && (
          <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Showing {displayList.length} result{displayList.length !== 1 ? 's' : ''} for "{searchQ}"</span>
            <button
              onClick={() => setSearchQ('')}
              style={{ background: 'none', border: 'none', color: '#0d9488', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              ✕ Clear
            </button>
          </div>
        )}

        {/* Test list */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: 360, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {displayList.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {isSearchMode
                ? `No tests found for "${searchQ}"`
                : `No tests in ${activeCategory} category`}
            </div>
          ) : displayList.map(t => {
            const sel = selectedIds.includes(t.id);
            return (
              <label key={t.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 11px', borderRadius: 8, cursor: 'pointer',
                background: sel ? '#ccfbf1' : '#f8fafc',
                border: sel ? '1.5px solid #0d9488' : '1.5px solid #e2e8f0',
                transition: 'all .12s',
              }}>
                <input
                  type="checkbox" checked={sel} onChange={() => toggle(t.id)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#0d9488' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                    {t.code}
                    {isSearchMode && t.category_name && (
                      <span style={{
                        marginLeft: 8, background: '#f1f5f9', padding: '1px 6px',
                        borderRadius: 4, color: '#64748b',
                      }}>{t.category_name}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {t.sample_type_display && (
                    <span style={{
                      fontSize: 11, color: '#64748b', background: '#f1f5f9',
                      padding: '2px 7px', borderRadius: 4,
                    }}>
                      {t.sample_type_display}
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── RIGHT: order panel ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Settings */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0' }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#1e293b' }}>Order Settings</div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Urgency</label>
            <select
              style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}
              value={urgency} onChange={e => setUrgency(e.target.value)}
            >
              <option value="ROUTINE">Routine</option>
              <option value="URGENT">⚡ Urgent</option>
              <option value="STAT">🚨 STAT (Immediate)</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Special Instructions</label>
            <textarea
              style={{
                width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0',
                borderRadius: 8, fontSize: 13, minHeight: 64, resize: 'vertical', boxSizing: 'border-box',
              }}
              placeholder="Fasting required, special handling…"
              value={instructions} onChange={e => setInstructions(e.target.value)}
            />
          </div>
        </div>

        {/* Selected summary */}
        <div style={{
          background: '#f0fdf4', borderRadius: 10, padding: 14,
          border: '1px solid #bbf7d0', flex: 1, overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#1e293b' }}>
            Selected Tests ({selectedTests.length})
          </div>
          {selectedTests.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 12 }}>No tests selected yet</div>
          ) : (
            <>
              {selectedTests.map(t => (
                <div key={t.id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 12, padding: '4px 0', borderBottom: '1px solid #dcfce7',
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{t.category_name}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => toggle(t.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, lineHeight: 1 }}
                    >×</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {success && (
          <div style={{ background: '#dcfce7', color: '#16a34a', padding: '8px 10px', borderRadius: 8, fontSize: 12 }}>
            {success}
          </div>
        )}

        <button
          onClick={handleOrder}
          disabled={submitting || selectedIds.length === 0}
          style={{
            background: selectedIds.length > 0 ? '#0d9488' : '#e2e8f0',
            color: selectedIds.length > 0 ? '#fff' : '#94a3b8',
            border: 'none', borderRadius: 8, padding: '11px 0',
            fontSize: 14, fontWeight: 700,
            cursor: selectedIds.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {submitting && <LoadingSpinner size={14} color="#fff" />}
          {submitting
            ? 'Placing Order…'
            : selectedIds.length > 0
              ? `🧪 Place Lab Order (${selectedIds.length} test${selectedIds.length > 1 ? 's' : ''})`
              : '🧪 Select tests to order'}
        </button>

        {/* Existing orders this visit */}
        {existingOrders.length > 0 && (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 8, letterSpacing: .5 }}>
              ORDERS THIS VISIT
            </div>
            {existingOrders.map(o => (
              <div key={o.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>Order #{o.id}</span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                    background: o.status === 'COMPLETED' ? '#dcfce7'
                               : o.status === 'PAID'      ? '#ccfbf1' : '#f1f5f9',
                    color:      o.status === 'COMPLETED' ? '#16a34a'
                               : o.status === 'PAID'      ? '#0d9488' : '#64748b',
                  }}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                  {o.items?.map(i => i.test_name).join(', ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── FLAG config ───────────────────────────────────────────────────────────────
const FLAG_CONFIG = {
  NORMAL:   { bg: '#dcfce7', color: '#166534', label: 'Normal'   },
  HIGH:     { bg: '#fee2e2', color: '#991b1b', label: 'High ↑'   },
  LOW:      { bg: '#fef9c3', color: '#854d0e', label: 'Low ↓'    },
  CRITICAL: { bg: '#fce7f3', color: '#9d174d', label: 'Critical !' },
};

const ORDER_STATUS_CONFIG = {
  ORDERED:          { bg: '#f1f5f9', color: '#475569' },
  BILLED:           { bg: '#fef9c3', color: '#854d0e' },
  PAID:             { bg: '#dbeafe', color: '#1e40af' },
  SAMPLE_COLLECTED: { bg: '#ede9fe', color: '#5b21b6' },
  IN_PROGRESS:      { bg: '#fef3c7', color: '#92400e' },
  COMPLETED:        { bg: '#dcfce7', color: '#166534' },
  CANCELLED:        { bg: '#fee2e2', color: '#991b1b' },
};

const LabResultsPanel = ({ recordId }) => {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!recordId) return;
    setLoading(true);
    setError('');

    // Step 1: fetch order list for this visit
    labAPI.getOrders({ opd_record_id: recordId })
      .then(async (data) => {
        const list = Array.isArray(data) ? data : (data.results || []);

        // Step 2: fetch DETAIL for every order so we get results + parameters
        const detailed = await Promise.all(
          list.map(o =>
            labAPI.getOrder(o.id).catch(() => o)  // fall back to summary on error
          )
        );

        setOrders(detailed);

        // Auto-expand completed orders
        const init = {};
        detailed.forEach(o => { if (o.status === 'COMPLETED') init[o.id] = true; });
        setExpanded(init);
      })
      .catch(() => setError('Failed to load lab orders.'))
      .finally(() => setLoading(false));
  }, [recordId]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <LoadingSpinner size={26} />
      <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 13 }}>Loading lab results…</div>
    </div>
  );

  if (error) return <div style={styles.alertError}>{error}</div>;

  if (orders.length === 0) return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: '#94a3b8' }}>
      <div style={{ fontSize: 40, marginBottom: 10 }}>🧪</div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>No lab orders for this visit</div>
      <div style={{ fontSize: 12, marginTop: 4 }}>
        Orders placed from the Test Order tab will appear here once results are entered.
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {orders.map(order => {
        const isOpen    = !!expanded[order.id];
        const statusCfg = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.ORDERED;

        return (
          <div key={order.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>

            {/* ── Order header ── */}
            <div
              onClick={() => toggle(order.id)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', cursor: 'pointer',
                background: isOpen ? '#f0fdf4' : '#f8fafc',
                borderBottom: isOpen ? '1px solid #e2e8f0' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🧪</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                    Order #{order.id}
                    {order.urgency && order.urgency !== 'ROUTINE' && (
                      <span style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', borderRadius: 4,
                        background: order.urgency === 'STAT' ? '#fee2e2' : '#fef9c3',
                        color:      order.urgency === 'STAT' ? '#991b1b' : '#854d0e',
                      }}>{order.urgency}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {(order.items || []).map(i => i.test_name).join(' · ')}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                  background: statusCfg.bg, color: statusCfg.color,
                }}>
                  {order.status?.replace(/_/g, ' ')}
                </span>
                <span style={{ color: '#94a3b8', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {/* ── Expanded body ── */}
            {isOpen && (
              <div style={{ padding: '14px 16px' }}>
                {(order.items || []).map((item, iIdx) => {

                  // ── KEY FIX: match results to this item's parameters by ID ──
                  const paramIds   = (item.parameters || []).map(p => p.id);
                  const itemResults = (order.results  || []).filter(r =>
                    paramIds.includes(r.parameter)
                  );

                  return (
                    <div key={item.id || iIdx} style={{ marginBottom: 16 }}>

                      {/* Test sub-header */}
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: '#0d9488',
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        marginBottom: 8, paddingBottom: 4,
                        borderBottom: '2px solid #ccfbf1',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <span>🔬</span> {item.test_name}
                        <span style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', textTransform: 'none' }}>
                          ({item.test_code})
                        </span>
                      </div>

                      {itemResults.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '4px 8px' }}>
                          Results not yet entered by lab technician.
                        </div>
                      ) : (
                        <>
                          {/* Column headers */}
                          <div style={{
                            display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr 80px 1fr',
                            gap: 8, padding: '4px 10px', marginBottom: 4,
                          }}>
                            {['Parameter', 'Result', 'Normal Range', 'Flag', 'Notes'].map(h => (
                              <div key={h} style={{
                                fontSize: 10, fontWeight: 700, color: '#94a3b8',
                                textTransform: 'uppercase', letterSpacing: 0.5,
                              }}>{h}</div>
                            ))}
                          </div>

                          {/* Result rows */}
                          {itemResults.map((res, rIdx) => {
                            const param     = (item.parameters || []).find(p => p.id === res.parameter);
                            const flagCfg   = FLAG_CONFIG[res.flag] || FLAG_CONFIG.NORMAL;
                            const isAbnormal = res.flag && res.flag !== 'NORMAL';

                            // Show numeric value if present, else text value
                            const displayValue =
                              (res.value_numeric !== null && res.value_numeric !== undefined && res.value_numeric !== '')
                                ? res.value_numeric
                                : (res.value_text || '—');

                            return (
                              <div key={res.id || rIdx} style={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1.5fr 80px 1fr',
                                gap: 8, alignItems: 'center',
                                padding: '8px 10px', borderRadius: 7, marginBottom: 4,
                                background: isAbnormal ? flagCfg.bg + '66' : '#f8fafc',
                                border: `1px solid ${isAbnormal ? flagCfg.color + '55' : '#e2e8f0'}`,
                              }}>
                                {/* Parameter name */}
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                                  {param?.name || `Parameter #${res.parameter}`}
                                </div>

                                {/* Value + unit */}
                                <div style={{ fontSize: 14, fontWeight: 700, color: isAbnormal ? flagCfg.color : '#0d9488' }}>
                                  {String(displayValue)}
                                  {param?.unit && (
                                    <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginLeft: 4 }}>
                                      {param.unit}
                                    </span>
                                  )}
                                </div>

                                {/* Normal range */}
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {param?.normal_range_text ||
                                    (param?.normal_min != null && param?.normal_max != null
                                      ? `${param.normal_min} – ${param.normal_max}`
                                      : '—')}
                                </div>

                                {/* Flag badge */}
                                <div>
                                  <span style={{
                                    fontSize: 10, fontWeight: 700,
                                    padding: '3px 8px', borderRadius: 20,
                                    background: flagCfg.bg, color: flagCfg.color,
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {flagCfg.label}
                                  </span>
                                </div>

                                {/* Technician notes */}
                                <div style={{
                                  fontSize: 11, color: '#64748b', fontStyle: 'italic',
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                  {res.notes || '—'}
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  );
                })}

                {/* Sample info footer */}
                {order.sample && (
                  <div style={{
                    marginTop: 10, paddingTop: 8, borderTop: '1px solid #f1f5f9',
                    fontSize: 11, color: '#94a3b8', display: 'flex', gap: 16, flexWrap: 'wrap',
                  }}>
                    <span>🧫 Sample: <strong style={{ color: '#475569' }}>
                      {order.sample.sample_type_display || order.sample.sample_type}
                    </strong></span>
                    {order.sample.barcode && (
                      <span>Barcode: <strong style={{ color: '#475569', fontFamily: 'monospace' }}>
                        {order.sample.barcode}
                      </strong></span>
                    )}
                    {order.sample.collected_at && (
                      <span>Collected: <strong style={{ color: '#475569' }}>
                        {formatDateTime(order.sample.collected_at)}
                      </strong></span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ConsultationModal = ({ record: initialRecord, token, onClose, onSaved, departments: propDepartments }) => {
  const [record,   setRecord]   = useState(initialRecord || null);
  const [fetching, setFetching] = useState(!initialRecord);
  const [activeTab, setActiveTab] = useState("vitals");
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  // Fetch record if not passed in
  useEffect(() => {
    if (initialRecord) return;
    const recordId = token?.opd_record_id || token?.opd_record?.id || token?.record?.id || token?.record_data?.id;
    if (!recordId) { setError("No OPD record found for this token. Please refresh the queue."); setFetching(false); return; }
    receptionAPI.getOPDRecord(recordId)
      .then(data => { setRecord(data); setFetching(false); })
      .catch(() => { setError("Failed to load consultation record."); setFetching(false); });
  }, []); // eslint-disable-line

  // ── Vitals ────────────────────────────────────────────────────────────────
  const [vitals, setVitals] = useState({
    bp_sys: record?.vitals?.bp_sys || "", bp_dia: record?.vitals?.bp_dia || "",
    pulse:  record?.vitals?.pulse  || "", temp:   record?.vitals?.temp   || "",
    temp_unit: record?.vitals?.temp_unit || "F",
    spo2:   record?.vitals?.spo2   || "", rr:     record?.vitals?.rr     || "",
    weight_kg: record?.vitals?.weight_kg || "", height_cm: record?.vitals?.height_cm || "",
  });

  // ── History ───────────────────────────────────────────────────────────────
  const [duration, setDuration] = useState(record?.symptoms?.duration || "");
  const [severity, setSeverity] = useState(record?.symptoms?.severity || "");
  const [assocSx,  setAssocSx]  = useState(record?.symptoms?.associated_symptoms || []);
  const [sxNotes,  setSxNotes]  = useState(record?.symptoms?.symptom_notes || "");

  // ── Examination ───────────────────────────────────────────────────────────
  const [exam, setExam] = useState({
    general_appearance: record?.examination?.general_appearance || "",
    cvs: record?.examination?.cvs || "", rs: record?.examination?.rs || "",
    cns: record?.examination?.cns || "", abdomen: record?.examination?.abdomen || "",
    others: record?.examination?.others || "", examination_notes: record?.examination?.examination_notes || "",
  });

  // ── Diagnosis ─────────────────────────────────────────────────────────────
  const [primaryDx,   setPrimaryDx]   = useState(record?.diagnosis || "");
  const [diffDx,      setDiffDx]      = useState(record?.diagnosis_detail?.differential?.join("\n") || "");
  const [icd10Codes,  setIcd10Codes]  = useState(record?.diagnosis_detail?.icd10_codes || []);

  // ── Prescription ──────────────────────────────────────────────────────────
  const [rxList, setRxList] = useState(record?.prescription || []);

  // ── Advice ────────────────────────────────────────────────────────────────
  const [advDietary,   setAdvDietary]   = useState(record?.advice_detail?.dietary       || "");
  const [advActivity,  setAdvActivity]  = useState(record?.advice_detail?.activity      || "");
  const [advWarning,   setAdvWarning]   = useState(record?.advice_detail?.warning_signs || "");
  const [advGeneral,   setAdvGeneral]   = useState(record?.advice_detail?.general || record?.advice || "");
  const [followUpDate, setFollowUpDate] = useState(record?.follow_up_date         || "");
  const [followUpInstr,setFollowUpInstr]= useState(record?.follow_up_instructions || "");

  // ── Referral (ENHANCED) ───────────────────────────────────────────────────
  const [refType,       setRefType]       = useState(record?.referral?.type        || "");
  const [refTo,         setRefTo]         = useState(record?.referral?.referred_to || "");
  const [refReason,     setRefReason]     = useState(record?.referral?.reason      || "");
  const [refUrgency,    setRefUrgency]    = useState(record?.referral?.urgency     || "routine");
  const [refDoctorId,   setRefDoctorId]   = useState(record?.referral?.transferred_to_doctor_id || "");
  const [refDoctorName, setRefDoctorName] = useState(record?.referral?.transferred_to_doctor_name || "");
  const [refDeptId,     setRefDeptId]     = useState("");
  const [refDoctorSearch, setRefDoctorSearch] = useState("");

  // Referral data loaders
  const [sameDeptDoctors, setSameDeptDoctors] = useState([]);
  const [refDeptDoctors,  setRefDeptDoctors]  = useState([]);
  const [loadingRefData,  setLoadingRefData]  = useState(false);
  const [departments,     setDepartments]     = useState(propDepartments || []);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const [doctorNotes, setDoctorNotes] = useState(record?.doctor_notes || "");
  const dirtySections = useRef(new Set());
  const markDirty = (section) => dirtySections.current.add(section);

  // ── BMI ───────────────────────────────────────────────────────────────────
  const bmi    = calcBMI(vitals.weight_kg, vitals.height_cm);
  const bmiCat = getBMICategory(bmi);

  // ── Load departments for referral ─────────────────────────────────────────
  useEffect(() => {
    if (departments.length === 0) {
      departmentAPI.getDepartments()
        .then(data => setDepartments(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  // ── Load same-dept doctors when "specialist" selected ────────────────────
  useEffect(() => {
    if (refType !== "specialist") { setSameDeptDoctors([]); return; }
    const deptId = record?.department;
    if (!deptId) return;
    setLoadingRefData(true);
    receptionAPI.getDoctorsForReferral
      ? receptionAPI.getDoctorsForReferral(deptId).then(data => setSameDeptDoctors(Array.isArray(data) ? data : [])).catch(() => setSameDeptDoctors([])).finally(() => setLoadingRefData(false))
      : receptionAPI.getDoctorsByDepartment(deptId).then(data => setSameDeptDoctors(Array.isArray(data) ? data : [])).catch(() => setSameDeptDoctors([])).finally(() => setLoadingRefData(false));
  }, [refType, record?.department]);

  // ── Load doctors for selected dept in "department" referral ──────────────
  useEffect(() => {
    if (refType !== "department" || !refDeptId) { setRefDeptDoctors([]); return; }
    setLoadingRefData(true);
    (receptionAPI.getDoctorsForReferral
      ? receptionAPI.getDoctorsForReferral(refDeptId)
      : receptionAPI.getDoctorsByDepartment(refDeptId)
    ).then(data => setRefDeptDoctors(Array.isArray(data) ? data : []))
     .catch(() => setRefDeptDoctors([]))
     .finally(() => setLoadingRefData(false));
  }, [refType, refDeptId]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleAssocSx = (sx) => setAssocSx(prev => prev.includes(sx) ? prev.filter(s => s !== sx) : [...prev, sx]);
  const updateExam    = (field) => (e) => setExam(prev => ({ ...prev, [field]: e.target.value }));
  const updateRx      = (idx, val) => setRxList(prev => prev.map((x,i) => i===idx ? val : x));
  const removeRx      = (idx)      => setRxList(prev => prev.filter((_,i) => i!==idx));
  const addRx         = ()         => setRxList(prev => [...prev, emptyMedication()]);

  const selectReferralDoctor = (doc) => {
    const name = doc.full_name || `${doc.first_name||""} ${doc.last_name||""}`.trim();
    setRefDoctorId(doc.id);
    setRefDoctorName(`Dr. ${name}`);
    setRefTo(`Dr. ${name}`);
  };

  // Filtered doctor lists for referral search
  const filteredSameDept = sameDeptDoctors.filter(d => {
    if (!refDoctorSearch) return true;
    const q = refDoctorSearch.toLowerCase();
    const name = (d.full_name || `${d.first_name||""} ${d.last_name||""}`).toLowerCase();
    return name.includes(q) || (d.designation||"").toLowerCase().includes(q);
  });
  const filteredRefDept = refDeptDoctors.filter(d => {
    if (!refDoctorSearch) return true;
    const q = refDoctorSearch.toLowerCase();
    const name = (d.full_name || `${d.first_name||""} ${d.last_name||""}`).toLowerCase();
    return name.includes(q) || (d.designation||"").toLowerCase().includes(q);
  });

  const buildPayload = (complete = false) => {
    const payload = {};

    // ── Vitals: only include if at least one numeric field is filled ─────────
    const vitalsPayload = {};
    const numericFields = ['bp_sys','bp_dia','pulse','spo2','rr','weight_kg','height_cm','temp'];
    Object.entries(vitals).forEach(([key, val]) => {
      if (val !== '' && val !== null && val !== undefined) {
        vitalsPayload[key] = numericFields.includes(key) ? parseFloat(val) : val;
      }
    });
    if (Object.keys(vitalsPayload).length > 0) {
      payload.vitals = vitalsPayload;
    }

    // ── Symptoms: only include if at least one field is non-empty ────────────
    const hasSymptomsData = duration.trim() || severity || assocSx.length > 0 || sxNotes.trim();
    if (hasSymptomsData) {
      payload.symptoms = {
        duration,
        severity,
        associated_symptoms: assocSx,
        symptom_notes: sxNotes,
      };
    }

    // ── Examination: only include if at least one field is non-empty ─────────
    const hasExamData = Object.values(exam).some(v => v && v.trim() !== '');
    if (hasExamData) {
      payload.examination = exam;
    }

    // ── Diagnosis: only include if primary diagnosis is filled ───────────────
    if (primaryDx.trim()) {
      payload.diagnosis        = primaryDx;
      payload.diagnosis_detail = {
        primary:      primaryDx,
        differential: diffDx.split('\n').map(s => s.trim()).filter(Boolean),
        icd10_codes:  icd10Codes,
      };
    }

    // ── Prescription: only include if ≥1 drug has a name ────────────────────
    const validRx = rxList.filter(med => med.name && med.name.trim() !== '');
    if (validRx.length > 0) {
      payload.prescription = validRx;
    }

    // ── Advice: only include if at least one advice field is non-empty ───────
    const hasAdviceData = advGeneral.trim() || advDietary.trim() || advActivity.trim() || advWarning.trim();
    if (hasAdviceData) {
      payload.advice        = advGeneral;
      payload.advice_detail = {
        dietary:      advDietary,
        activity:     advActivity,
        warning_signs: advWarning,
        general:      advGeneral,
      };
    }

    // ── Follow-up: only if a date is set ────────────────────────────────────
    if (followUpDate) {
      payload.follow_up_date = followUpDate;
    }
    if (followUpInstr.trim()) {
      payload.follow_up_instructions = followUpInstr;
    }

    // ── Referral: only if a type AND destination are set ────────────────────
    if (refType && refTo.trim()) {
      payload.referral = {
        type:        refType,
        referred_to: refTo,
        reason:      refReason,
        urgency:     refUrgency,
        ...(refDoctorId ? { doctor_id: refDoctorId, doctor_name: refDoctorName } : {}),
        ...(refDeptId   ? { department_id: refDeptId }                           : {}),
      };
    }

    // ── Doctor notes: only if non-empty ─────────────────────────────────────
    if (doctorNotes.trim()) {
      payload.doctor_notes = doctorNotes;
    }

    // ── Completion flag ──────────────────────────────────────────────────────
    payload.is_consultation_complete = complete;

    return payload;
  };

  // ── Draft save ────────────────────────────────────────────────────────────
  const handleDraftSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await receptionAPI.saveOPDClinical(record.id, buildPayload(false));
      setSuccess(isAssignedToMe ? 'Draft saved.' : 'Your contribution has been saved and tagged with your name.');
      setRecord(updated);
      onSaved && onSaved(updated);
    } catch (e) {
      const d = e?.response?.data;
      setError(
        typeof d === 'string' ? d
        : d?.details ? JSON.stringify(d.details)
        : d?.error || 'Failed to save.'
      );
    } finally { setSaving(false); }
  };


  // ── Complete consultation ─────────────────────────────────────────────────
  const handleComplete = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const updated = await receptionAPI.saveOPDPrescription(record.id, buildPayload(true));
      setRecord(updated);

      // If referral with a specific doctor was selected → transfer responsibility
      if (refDoctorId && (refType === 'specialist' || refType === 'department')) {
        await receptionAPI.reassignOPDRecord(record.id, {
          doctor_id: refDoctorId,
          reason: refReason,
        });
      }

      setSuccess('Consultation completed successfully!');
      onSaved && onSaved(updated);
      setTimeout(() => onClose(), 1000);
    } catch (e) {
      const d = e?.response?.data;
      // Surface the backend's "only assigned doctor can complete" message clearly
      setError(
        d?.error || (typeof d === 'string' ? d : 'Failed to complete consultation.')
      );
    } finally { setSaving(false); }
  };


  // ── Loading / error states ────────────────────────────────────────────────
  if (fetching) {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.modal,alignItems:"center",justifyContent:"center",minHeight:300 }}>
          <LoadingSpinner size={36} />
          <div style={{ marginTop:16,color:COLOR.gray,fontSize:14 }}>Loading consultation record…</div>
        </div>
      </div>
    );
  }
  if (!record) {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.modal,padding:0 }}>
          <div style={styles.modalHeader}><div style={styles.heading}>OPD Consultation</div><CloseButton onClick={onClose} /></div>
          <div style={{ padding:32 }}>
            <div style={styles.alertError}>{error || "No OPD record found for this patient token."}</div>
            <button style={styles.btnOutline} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const patientName    = getPatientName(token) || record?.patient_name_snapshot || "Patient";
  const allergies      = record?.patient_allergies || record?.patient?.allergies;
  const isCompleted    = record?.is_consultation_complete;
  const isAssignedToMe = record?.is_assigned_to_me ?? true; // default true so non-context renders still work
  const contributors   = record?.contributors || {};

  const tabsWithCount = CONSULTATION_TABS.map(t => ({
    ...t,
    label: t.id === "inv" ? 'Test Order'
         : t.id === "rx"  && rxList.length  > 0 ? `Rx (${rxList.length})`
         : t.label,
  }));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:10 }}>
              <div style={styles.heading}>OPD Consultation</div>
              {isCompleted && (
                <span style={{ padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700,color:"#166534",background:"#dcfce7" }}>✓ Completed</span>
              )}
              {!isAssignedToMe && !isCompleted && (
                <span style={{ padding:"3px 10px",borderRadius:999,fontSize:11,fontWeight:700,color:"#1e40af",background:"#dbeafe" }}>Contributing</span>
              )}
            </div>
            <div style={{ fontSize:12,color:COLOR.gray,marginTop:3 }}>
              {patientName}
              {record?.token_number && ` · Token #${record.token_number}`}
              {record?.assigned_doctor_name && (
                <span style={{ color:COLOR.assigned }}> · Assigned: {record.assigned_doctor_name}</span>
              )}
            </div>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        {/* Allergy Alert */}
        {allergies && (
          <div style={{ padding:"8px 24px 0",flexShrink:0 }}>
            <div style={styles.alertWarning}>⚠️ <strong>Known Allergies:</strong> {allergies}</div>
          </div>
        )}

        {/* Assigned-to-me info banner for contributing doctors */}
        {/* Role banner — tells contributing doctors exactly what they can do */}
        {!isAssignedToMe && !isCompleted && (
          <div style={{ padding: '8px 24px 0', flexShrink: 0 }}>
            <div style={{
              background: '#f0f9ff', border: '1px solid #7dd3fc',
              borderRadius: 8, padding: '10px 16px', fontSize: 13, color: '#0c4a6e',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>👨‍⚕️</span>
              <div>
                <strong>You are contributing to this consultation.</strong>
                <div style={{ marginTop: 3, fontSize: 12, color: '#0369a1' }}>
                  Your name and timestamp will be tagged to every section you save.
                  Only <strong>{record?.assigned_doctor_name || record?.doctor_name || 'the assigned doctor'}</strong> can
                  write the final prescription and mark the consultation as complete.
                  The patient will remain in the active queue until then.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Bar */}
        <div style={{ display:"flex",padding:"8px 24px 0",borderBottom:`1px solid ${COLOR.border}`,flexShrink:0,overflowX:"auto",gap:0 }}>
          {tabsWithCount.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding:"8px 16px",border:"none",cursor:"pointer",fontSize:13,fontWeight:activeTab===tab.id?700:400,color:activeTab===tab.id?COLOR.primary:COLOR.gray,background:"none",whiteSpace:"nowrap",flexShrink:0,borderBottom:activeTab===tab.id?`3px solid ${COLOR.primary}`:"3px solid transparent",transition:"all 0.15s",position:"relative" }}>
              {tab.label}
              {/* Dot indicator if section has contributor data */}
              {contributors[tab.id === "rx" ? "prescription" : tab.id === "inv" ? "investigations" : tab.id] && (
                <span style={{ position:"absolute",top:6,right:6,width:5,height:5,borderRadius:"50%",background:COLOR.primary }} />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={styles.modalBody}>
          {error   && <div style={styles.alertError}>{error}</div>}
          {success && <div style={styles.alertSuccess}>✓ {success}</div>}

          {/* ── VITALS TAB ── */}
          {activeTab === "vitals" && (
            <div>
              <ContributorBadge data={contributors.vitals} />
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Haemodynamics &amp; Oxygenation</div>
                <div style={{ ...styles.grid4,marginBottom:14 }}>
                  {[["bp_sys","Systolic BP (mmHg)","120"],["bp_dia","Diastolic BP (mmHg)","80"],["pulse","Pulse Rate (bpm)","72"],["spo2","SpO₂ (%)","98"]].map(([k,l,ph]) => (
                    <div key={k}><label style={styles.label}>{l}</label><input style={styles.input} type="number" placeholder={ph} value={vitals[k]} onChange={e => setVitals(v=>({...v,[k]:e.target.value}))} /></div>
                  ))}
                </div>
                <div style={styles.grid4}>
                  <div style={{ display:"flex",gap:6 }}>
                    <div style={{ flex:1 }}>
                      <label style={styles.label}>Temperature</label>
                      <input style={styles.input} type="number" step="0.1" placeholder="98.6" value={vitals.temp} onChange={e => setVitals(v=>({...v,temp:e.target.value}))} />
                    </div>
                    <div style={{ width:60,alignSelf:"flex-end" }}>
                      <select style={styles.select} value={vitals.temp_unit} onChange={e => setVitals(v=>({...v,temp_unit:e.target.value}))}>
                        <option value="F">°F</option><option value="C">°C</option>
                      </select>
                    </div>
                  </div>
                  {[["rr","Respiratory Rate (/min)","18"],["weight_kg","Weight (kg)","70"],["height_cm","Height (cm)","170"]].map(([k,l,ph]) => (
                    <div key={k}><label style={styles.label}>{l}</label><input style={styles.input} type="number" step={k==="weight_kg"?"0.1":undefined} placeholder={ph} value={vitals[k]} onChange={e => setVitals(v=>({...v,[k]:e.target.value}))} /></div>
                  ))}
                </div>
              </div>
            {bmi && (
                <div style={{ background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,padding:"14px 18px",display:"flex",alignItems:"center",gap:16 }}>
                  <div style={{ fontSize:36,fontWeight:900,color:bmiCat.color }}>{bmi}</div>
                  <div>
                    <div style={{ fontWeight:700,color:bmiCat.color,fontSize:15 }}>{bmiCat.label}</div>
                    <div style={styles.small}>BMI from {vitals.weight_kg} kg / {vitals.height_cm} cm</div>
                  </div>
                  <div style={{ marginLeft:"auto",textAlign:"right" }}>
                    <div style={styles.small}>Normal: 18.5–24.9</div>
                    <div style={styles.small}>Overweight: 25–29.9 | Obese: ≥30</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── HISTORY TAB ── */}
          {activeTab === "symptoms" && (
            <div>
              <ContributorBadge data={contributors.symptoms} />
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Chief Complaint (recorded at reception)</div>
                <div style={{ background:"#fff",border:`1px solid ${COLOR.border}`,borderRadius:7,padding:"10px 14px",fontSize:13,color:COLOR.textSub }}>
                  {record?.chief_complaint || <em style={{ color:COLOR.lightGray }}>Not recorded</em>}
                </div>
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>History of Presenting Illness</div>
                <div style={{ ...styles.grid2,marginBottom:14 }}>
                  <div>
                    <label style={styles.label}>Duration of Symptoms</label>
                    <input style={styles.input} placeholder="e.g. 3 days, 2 weeks" value={duration} onChange={e => setDuration(e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Severity</label>
                    <select style={styles.select} value={severity} onChange={e => setSeverity(e.target.value)}>
                      <option value="">— Select severity —</option>
                      <option value="mild">Mild</option>
                      <option value="moderate">Moderate</option>
                      <option value="severe">Severe</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={styles.label}>Associated Symptoms</label>
                  <div style={{ display:"flex",flexWrap:"wrap",gap:6,marginTop:4 }}>
                    {ASSOCIATED_SYMPTOMS.map(sx => {
                      const checked = assocSx.includes(sx);
                      return (
                        <label key={sx} style={{ display:"flex",alignItems:"center",gap:5,fontSize:12,cursor:"pointer",userSelect:"none",padding:"4px 10px",borderRadius:6,background:checked?"#dbeafe":COLOR.bgSection,border:`1px solid ${checked?"#93c5fd":COLOR.border}`,color:checked?"#1e40af":COLOR.textSub,fontWeight:checked?600:400,transition:"all 0.12s" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleAssocSx(sx)} style={{ cursor:"pointer" }} />{sx}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label style={styles.label}>Additional Notes (Past Medical / Family / Social History)</label>
                  <textarea style={styles.textarea} placeholder="Past medical history, surgical history, family history…" value={sxNotes} onChange={e => setSxNotes(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* ── EXAMINATION TAB ── */}
          {activeTab === "examination" && (
            <div>
              <ContributorBadge data={contributors.examination} />
              <div style={styles.section}>
                <div style={styles.sectionTitle}>General Appearance</div>
                <textarea style={styles.textarea} placeholder="e.g. Conscious" value={exam.general_appearance} onChange={updateExam("general_appearance")} />
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>System-wise Examination</div>
                <div style={{ ...styles.grid2,marginBottom:14 }}>
                  {[["cvs","CVS (Cardiovascular System)","Cardiovascular System..."],["rs","RS (Respiratory System)","Respiratory System..."]].map(([k,l,ph]) => (
                    <div key={k}><label style={styles.label}>{l}</label><textarea style={{ ...styles.textarea,minHeight:64 }} placeholder={ph} value={exam[k]} onChange={updateExam(k)} /></div>
                  ))}
                </div>
                <div style={{ ...styles.grid2,marginBottom:14 }}>
                  {[["cns","CNS (Central Nervous System)","Central Nervous System..."],["abdomen","Abdomen","Abdomen..."]].map(([k,l,ph]) => (
                    <div key={k}><label style={styles.label}>{l}</label><textarea style={{ ...styles.textarea,minHeight:64 }} placeholder={ph} value={exam[k]} onChange={updateExam(k)} /></div>
                  ))}
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={styles.label}>Others (MSK / Skin / ENT / Ophthalmology / Local)</label>
                  <textarea style={styles.textarea} placeholder="skin, ENT findings…" value={exam.others} onChange={updateExam("others")} />
                </div>
                <div>
                  <label style={styles.label}>Additional Examination Notes</label>
                  <textarea style={styles.textarea} placeholder="Any other relevant observations…" value={exam.examination_notes} onChange={updateExam("examination_notes")} />
                </div>
              </div>
            </div>
          )}

          {/* ── DIAGNOSIS TAB ── */}
          {activeTab === "diagnosis" && (
            <div>
              <ContributorBadge data={contributors.diagnosis} />
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Primary / Final Diagnosis *</div>
                <textarea style={{ ...styles.textarea,minHeight:88 }} placeholder="Enter primary diagnosis…" value={primaryDx} onChange={e => setPrimaryDx(e.target.value)} />
                <div style={{ ...styles.small,marginTop:4 }}>Required to mark consultation as complete.</div>
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Differential Diagnosis</div>
                <textarea style={styles.textarea} placeholder={"Viral fever, Typhoid fever...."} value={diffDx} onChange={e => setDiffDx(e.target.value)} />
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>ICD-10 Codes</div>
                <ICD10Picker selected={icd10Codes} onAdd={c => setIcd10Codes(prev=>[...prev,c])} onRemove={code => setIcd10Codes(prev=>prev.filter(c=>c.code!==code))} />
              </div>
            </div>
          )}

          {/* ── INVESTIGATIONS TAB ── */}
          {activeTab === "inv" && (
            <TestOrderingPanel recordId={record?.id} patientName={token?.patient_name} />
          )}

          {/* ── PRESCRIPTION TAB ── */}
          {activeTab === "rx" && (
            <div>
              <ContributorBadge data={contributors.prescription} />
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14 }}>
                <div style={{ ...styles.sectionTitle,margin:0 }}>Prescription</div>
                <button style={styles.btnPrimary} onClick={addRx}>+ Add Medication</button>
              </div>
              {rxList.length === 0 ? (
                <div style={{ textAlign:"center",padding:"44px 24px",background:COLOR.bgSection,border:`2px dashed ${COLOR.border}`,borderRadius:10,color:COLOR.lightGray }}>
                  <div style={{ fontSize:32,marginBottom:10 }}>💊</div>
                  <div style={{ marginBottom:14 }}>No medications added yet.</div>
                  <button style={styles.btnPrimary} onClick={addRx}>+ Add First Medication</button>
                </div>
              ) : rxList.map((med,i) => <MedicationRow key={i} med={med} index={i} onChange={updateRx} onRemove={removeRx} />)}
            </div>
          )}

          {/* ── ADVICE TAB ── */}
          {activeTab === "advice" && (
            <div>
              <ContributorBadge data={contributors.advice} />
              {[
                ["🥗 Dietary Advice",advDietary,setAdvDietary,"Low salt diet, avoid spicy food…"],
                ["🏃 Activity Restrictions",advActivity,setAdvActivity,"Bed rest for 3 days, avoid heavy exercise…"],
              ].map(([title,val,setter,ph]) => (
                <div key={title} style={styles.section}>
                  <div style={styles.sectionTitle}>{title}</div>
                  <textarea style={styles.textarea} placeholder={ph} value={val} onChange={e=>setter(e.target.value)} />
                </div>
              ))}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>⚠️ Warning Signs — Return Immediately If…</div>
                <textarea style={styles.textarea} placeholder="Fever > 103°F, severe chest pain, difficulty breathing…" value={advWarning} onChange={e=>setAdvWarning(e.target.value)} />
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>📋 General Instructions</div>
                <textarea style={styles.textarea} placeholder="Complete the full antibiotic course, avoid self-medication…" value={advGeneral} onChange={e=>setAdvGeneral(e.target.value)} />
              </div>
              <div style={styles.section}>
                <div style={styles.sectionTitle}>📅 Follow-up Schedule</div>
                <div style={styles.grid2}>
                  <div>
                    <label style={styles.label}>Follow-up Date</label>
                    <input style={styles.input} type="date" value={followUpDate} min={new Date().toISOString().split("T")[0]} onChange={e=>setFollowUpDate(e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Follow-up Instructions</label>
                    <input style={styles.input} placeholder="e.g. Review with blood reports" value={followUpInstr} onChange={e=>setFollowUpInstr(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── REFERRAL TAB (ENHANCED) ── */}
          {activeTab === "referral" && (
            <div>
              <ContributorBadge data={contributors.referral} />

              {/* Referral type */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Referral Type</div>
                <select style={{ ...styles.select,maxWidth:400,marginBottom:16 }} value={refType}
                  onChange={e => { setRefType(e.target.value); setRefDoctorId(""); setRefDoctorName(""); setRefTo(""); setRefDeptId(""); setRefDoctorSearch(""); }}>
                  <option value="">— No referral required —</option>
                  <option value="specialist">Refer to Specialist (Same Department)</option>
                  <option value="department">Refer to Another Department</option>
                  <option value="external">Refer to External Facility</option>
                </select>

                {/* ── Specialist: same-department doctor picker ── */}
                {refType === "specialist" && (
                  <div>
                    <div style={{ ...styles.sectionTitle,marginBottom:8 }}>Select Doctor (Same Department)</div>
                    {loadingRefData ? (
                      <div style={{ display:"flex",alignItems:"center",gap:8,color:COLOR.gray,fontSize:13,padding:"12px 0" }}><LoadingSpinner size={16} /> Loading doctors…</div>
                    ) : (
                      <>
                        <input style={{ ...styles.input,marginBottom:10 }} placeholder="Search by name or designation…" value={refDoctorSearch} onChange={e=>setRefDoctorSearch(e.target.value)} />
                        {filteredSameDept.length === 0 ? (
                          <div style={{ color:COLOR.gray,fontSize:13,padding:"8px 0" }}>No doctors found in this department.</div>
                        ) : (
                          <div style={{ maxHeight:300,overflowY:"auto" }}>
                            {filteredSameDept.map(doc => (
                              <DoctorOptionCard key={doc.id} doctor={doc} selected={refDoctorId === doc.id} onSelect={() => selectReferralDoctor(doc)} />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {refDoctorName && (
                      <div style={{ ...styles.alertSuccess,marginTop:10 }}>✓ Selected: <strong>{refDoctorName}</strong>. Responsibility will transfer on completion.</div>
                    )}
                  </div>
                )}

                {/* ── Department: dept picker → doctor picker ── */}
                {refType === "department" && (
                  <div>
                    <div style={{ marginBottom:16 }}>
                      <label style={styles.label}>Select Department</label>
                      <select style={{ ...styles.select,maxWidth:400 }} value={refDeptId}
                        onChange={e => { setRefDeptId(e.target.value); setRefDoctorId(""); setRefDoctorName(""); setRefTo(""); setRefDoctorSearch(""); }}>
                        <option value="">— Choose department —</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>
                    {refDeptId && (
                      <>
                        <div style={{ ...styles.sectionTitle,marginBottom:8 }}>Select Doctor in {departments.find(d=>String(d.id)===String(refDeptId))?.name || "Department"}</div>
                        {loadingRefData ? (
                          <div style={{ display:"flex",alignItems:"center",gap:8,color:COLOR.gray,fontSize:13,padding:"12px 0" }}><LoadingSpinner size={16} /> Loading doctors…</div>
                        ) : (
                          <>
                            <input style={{ ...styles.input,marginBottom:10 }} placeholder="Search by name or designation…" value={refDoctorSearch} onChange={e=>setRefDoctorSearch(e.target.value)} />
                            {filteredRefDept.length === 0 ? (
                              <div style={{ color:COLOR.gray,fontSize:13,padding:"8px 0" }}>No doctors available in this department.</div>
                            ) : (
                              <div style={{ maxHeight:300,overflowY:"auto" }}>
                                {filteredRefDept.map(doc => (
                                  <DoctorOptionCard key={doc.id} doctor={doc} selected={refDoctorId === doc.id} onSelect={() => selectReferralDoctor(doc)} />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                        {refDoctorName && (
                          <div style={{ ...styles.alertSuccess,marginTop:10 }}>✓ Selected: <strong>{refDoctorName}</strong>. Full responsibility will transfer to this doctor on completion.</div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── External: free-text ── */}
                {refType === "external" && (
                  <div>
                    <label style={styles.label}>Facility / Specialist Name</label>
                    <input style={{ ...styles.input,maxWidth:420 }} placeholder="e.g. City General Hospital, Dr. Sharma (Nephrologist)" value={refTo} onChange={e=>setRefTo(e.target.value)} />
                  </div>
                )}
              </div>

              {/* Common fields — urgency + reason (always shown when refType set) */}
              {refType && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Referral Details</div>
                  <div style={{ ...styles.grid2,marginBottom:14 }}>
                    <div>
                      <label style={styles.label}>Urgency</label>
                      <select style={styles.select} value={refUrgency} onChange={e=>setRefUrgency(e.target.value)}>
                        <option value="routine">Routine</option>
                        <option value="urgent">Urgent</option>
                        <option value="emergency">Emergency</option>
                      </select>
                    </div>
                    <div>
                      <label style={styles.label}>Referred To (display name)</label>
                      <input style={styles.input} value={refTo} onChange={e=>setRefTo(e.target.value)} placeholder="Auto-filled on doctor selection" />
                    </div>
                  </div>
                  <div>
                    <label style={styles.label}>Reason for Referral</label>
                    <textarea style={{ ...styles.textarea,minHeight:100 }} placeholder="Clinical reason, brief history summary, specific query for receiving doctor…" value={refReason} onChange={e=>setRefReason(e.target.value)} />
                  </div>
                </div>
              )}

              {!refType && <div style={styles.small}>Select a referral type above to activate this section.</div>}
            </div>
          )}

          {/* ── NOTES TAB ── */}
          {activeTab === "notes" && (
            <div style={styles.section}>
              <ContributorBadge data={contributors.notes} />
              <div style={styles.sectionTitle}>Internal Doctor Notes</div>
              <textarea style={{ ...styles.textarea,minHeight:180 }} placeholder="Internal notes — not printed on prescription or visible to patient…" value={doctorNotes} onChange={e=>setDoctorNotes(e.target.value)} />
              <div style={{ ...styles.small,marginTop:6 }}>These notes are for internal record-keeping only.</div>
            </div>
          )}
          {activeTab === 'test-results' && (
            <LabResultsPanel recordId={record?.id} />
          )}
        </div>

        {/* Footer */}
        <div style={styles.modalFooter}>
          <button style={styles.btnOutline} onClick={onClose} disabled={saving}>
            Cancel
          </button>

          {/* Save button — available to EVERYONE (assigned + contributing) */}
          <button
            style={{ ...styles.btnNeutral, opacity: saving ? 0.7 : 1 }}
            onClick={handleDraftSave}
            disabled={saving}
          >
            {saving && <LoadingSpinner size={14} color="#fff" />}
            {isAssignedToMe ? 'Save Draft' : '💾 Save My Contribution'}
          </button>

          {/* Complete button — ASSIGNED DOCTOR ONLY */}
          {isAssignedToMe && (
            <button
              style={{
                ...styles.btnSuccess,
                background: isCompleted ? '#6b7280' : COLOR.success,
                opacity: saving ? 0.7 : 1,
                cursor: isCompleted ? 'default' : 'pointer',
              }}
              onClick={handleComplete}
              disabled={saving || isCompleted}
            >
              {saving && <LoadingSpinner size={14} color="#fff" />}
              {isCompleted
                ? '✓ Consultation Completed'
                : refDoctorId
                ? 'Complete & Transfer Patient'
                : 'Complete Consultation'}
            </button>
          )}

          {/* Placeholder shown to contributing doctors explaining why they can't complete */}
          {!isAssignedToMe && !isCompleted && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#64748b',
              padding: '8px 14px', background: '#f8fafc',
              border: '1px solid #e2e8f0', borderRadius: 8,
            }}>
              <span>🔒</span>
              Only {record?.assigned_doctor_name || 'the assigned doctor'} can complete this consultation
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// PATIENT DETAIL PANEL (right panel)
// =============================================================================

const PatientDetailPanel = ({ token, record, onStartConsultation, onViewHistory, onSkipPatient }) => {
  if (!token && !record) {
    return (
      <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",color:COLOR.lightGray,gap:12 }}>
        <div style={{ fontSize:52 }}>🏥</div>
        <div style={{ fontSize:15,fontWeight:500 }}>Select a patient from the queue</div>
        <div style={{ fontSize:13 }}>Click a token on the left to view patient details</div>
      </div>
    );
  }

  const name   = record?.patient_name_snapshot || getPatientName(token) || "Unknown Patient";
  const age    = record?.patient_age    || getPatientAge(token);
  const gender = record?.patient_gender || getPatientGender(token);
  const status = token?.status || "WAITING";   // ← use TOKEN status for badge
  const tnum   = record?.token_number   || token?.token_number;
  const doctor = record?.doctor_name    || token?.doctor_name;
  const dept   = record?.department_name || token?.department_name;
  const phone  = record?.patient_phone  || token?.patient?.phone;
  const assignedDoctorName = record?.assigned_doctor_name;
  const isTransferred = assignedDoctorName && assignedDoctorName !== doctor;

  const consultBtnLabel = record?.is_consultation_complete
    ? "📋 View / Edit Consultation"
    : (record?.vitals || record?.diagnosis)
    ? "✏️ Continue Consultation"
    : "🩺 Start Consultation";
  const consultBtnColor = record?.is_consultation_complete ? "#6b7280" : COLOR.primary;

  return (
    <div style={{ height:"100%",overflowY:"auto",padding:18 }}>
      {/* Patient header */}
      <div style={{ marginBottom:16 }}>
        <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:20,fontWeight:800,color:COLOR.text }}>{name}</div>
            <div style={{ fontSize:12,color:COLOR.gray,marginTop:3 }}>
              {age && `${age} yrs`}{gender && ` · ${gender}`}{phone && ` · ${phone}`}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <div style={{ fontSize:12,color:COLOR.textSub,marginTop:8,display:"flex",gap:16,flexWrap:"wrap" }}>
          {tnum   && <span><strong>Token:</strong> #{tnum}</span>}
          {doctor && <span><strong>Doctor:</strong> {doctor}</span>}
          {dept   && <span><strong>Dept:</strong> {dept}</span>}
        </div>
        {/* Transferred-to banner */}
        {isTransferred && (
          <div style={{ marginTop:8,background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:6,padding:"4px 10px",fontSize:12,color:"#1e40af" }}>
            🔄 Currently assigned to: <strong>{assignedDoctorName}</strong>
          </div>
        )}
      </div>

      {record?.patient_allergies && (
        <div style={styles.alertWarning}>⚠️ <strong>Known Allergies:</strong> {record.patient_allergies}</div>
      )}



      {record?.vitals && Object.keys(record.vitals).length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize:10,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>VITALS THIS VISIT</div>
          <VitalsDisplay vitals={record.vitals} />
        </div>
      )}

      {record?.diagnosis && (
        <div style={styles.card}>
          <div style={{ fontSize:10,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:5 }}>DIAGNOSIS</div>
          <div style={{ fontSize:13,fontWeight:600 }}>{record.diagnosis}</div>
        </div>
      )}

      {record?.prescription?.length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize:10,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>PRESCRIPTION ({record.prescription.length})</div>
          <PrescriptionDisplay prescription={record.prescription} />
        </div>
      )}

      {/* Contributors summary */}
      {record?.contributors && Object.keys(record.contributors).length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize:10,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:6 }}>SECTION CONTRIBUTORS</div>
          <div style={{ display:"flex",flexWrap:"wrap",gap:5 }}>
            {Object.entries(record.contributors).map(([section, info]) => (
              <span key={section} style={{ background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:5,padding:"2px 8px",fontSize:11,color:"#1e40af" }}>
                <strong style={{ textTransform:"capitalize" }}>{section}</strong>: {info.doctor_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {record?.follow_up_date && (
        <div style={styles.card}>
          <div style={{ fontSize:10,fontWeight:700,color:COLOR.gray,letterSpacing:1,marginBottom:5 }}>FOLLOW-UP</div>
          <div style={{ fontSize:13 }}>
            {formatDate(record.follow_up_date)}
            {typeof record.follow_up_days_remaining === "number" && (
              <span style={{ marginLeft:8,fontSize:11,fontWeight:600,color:record.follow_up_days_remaining < 0 ? COLOR.danger : COLOR.success }}>
                {record.follow_up_days_remaining < 0 ? `(${Math.abs(record.follow_up_days_remaining)} days overdue)` : `(in ${record.follow_up_days_remaining} days)`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display:"flex",flexDirection:"column",gap:10,marginTop:18 }}>
        <button style={{ ...styles.btnPrimary,background:consultBtnColor,justifyContent:"center",padding:"11px 20px",fontSize:14 }} onClick={onStartConsultation}>
          {consultBtnLabel}
        </button>
        <button style={{ ...styles.btnOutline,justifyContent:"center",padding:"11px 20px",fontSize:14 }} onClick={onViewHistory}>
          📁 View Previous Visits
        </button>
        {!record?.is_consultation_complete && onSkipPatient && (
          <button
            style={{ ...styles.btnOutline,justifyContent:"center",padding:"11px 20px",fontSize:14,borderColor:COLOR.danger,color:COLOR.danger }}
            onClick={onSkipPatient}
          >
            ⏭ Skip / No Show
          </button>
        )}
      </div>

      {(record?.consultation_started_at || record?.consultation_completed_at) && (
        <div style={{ marginTop:16,paddingTop:12,borderTop:`1px solid #f3f4f6` }}>
          {record.consultation_started_at  && <div style={styles.small}>Started: {formatDateTime(record.consultation_started_at)}</div>}
          {record.consultation_completed_at && <div style={{ ...styles.small,marginTop:3 }}>Completed: {formatDateTime(record.consultation_completed_at)}</div>}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// TOKEN QUEUE ITEM
// =============================================================================

const TokenQueueItem = ({ token, isSelected, onClick }) => {
  const rec    = extractRecord(token);
  const name   = rec?.patient_name_snapshot || getPatientName(token) || "—";
  const age    = rec?.patient_age    || getPatientAge(token);
  const gender = rec?.patient_gender || getPatientGender(token);
  // ← FIX: use token.status for queue management, NOT record.status
  const status = token?.status || "WAITING";
  const tnum   = rec?.token_number   || token?.token_number;
  const complaint = rec?.chief_complaint || token?.chief_complaint || "";

  const dotColor = { COMPLETED:"#10b981",IN_PROGRESS:"#f59e0b",WAITING:"#3b82f6",CANCELLED:"#ef4444" }[status] || "#9ca3af";

  return (
    <div onClick={onClick} style={{ padding:"12px 14px",cursor:"pointer",borderBottom:`1px solid #f3f4f6`,background:isSelected?"#eff6ff":"#fff",borderLeft:isSelected?`4px solid ${COLOR.primary}`:"4px solid transparent",transition:"all 0.12s" }}>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
        <div style={{ fontWeight:700,fontSize:14,color:COLOR.text }}>{name}</div>
        <div style={{ display:"flex",alignItems:"center",gap:5 }}>
          <span style={{ width:8,height:8,borderRadius:"50%",background:dotColor,flexShrink:0 }} />
          <span style={{ fontSize:11,color:COLOR.lightGray,fontWeight:500 }}>#{tnum}</span>
        </div>
      </div>
      <div style={{ fontSize:12,color:COLOR.gray,marginTop:2 }}>
        {age && `${age}y`}{gender && ` · ${gender}`}
        {complaint && <span style={{ fontStyle:"italic" }}> · {complaint.length>36?`${complaint.slice(0,36)}…`:complaint}</span>}
      </div>
      {rec?.diagnosis && (
        <div style={{ fontSize:11,color:COLOR.textSub,marginTop:2,fontWeight:500 }}>
          Dx: {rec.diagnosis.length>44?`${rec.diagnosis.slice(0,44)}…`:rec.diagnosis}
        </div>
      )}
      {/* Show if patient was transferred away */}
      {rec?.assigned_doctor_name && rec.assigned_doctor_name !== rec.doctor_name && (
        <div style={{ fontSize:10,color:COLOR.assigned,marginTop:2 }}>🔄 Now with {rec.assigned_doctor_name}</div>
      )}
    </div>
  );
};

// =============================================================================
// DEPARTMENT SEARCH RESULT ITEM (for dept search mode)
// =============================================================================

const DeptSearchResultItem = ({ record: rec, isSelected, onClick }) => (
  <div onClick={onClick} style={{ padding:"11px 14px",cursor:"pointer",borderBottom:`1px solid #f3f4f6`,background:isSelected?"#eff6ff":"#fff",borderLeft:isSelected?`4px solid ${COLOR.assigned}`:"4px solid transparent",transition:"all 0.12s" }}>
    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
      <div style={{ fontWeight:700,fontSize:14,color:COLOR.text }}>{rec.patient_name_snapshot}</div>
      <span style={{ fontSize:11,color:COLOR.lightGray,fontWeight:500 }}>#{rec.token_number}</span>
    </div>
    <div style={{ fontSize:12,color:COLOR.gray,marginTop:2 }}>
      {rec.patient_age && `${rec.patient_age}y`}
      {rec.patient_gender && ` · ${rec.patient_gender}`}
      {rec.chief_complaint && <span style={{ fontStyle:"italic" }}> · {rec.chief_complaint.length>36?`${rec.chief_complaint.slice(0,36)}…`:rec.chief_complaint}</span>}
    </div>
    <div style={{ fontSize:11,color:COLOR.assigned,marginTop:2 }}>
      {rec.assigned_doctor_name || rec.doctor_name} · {rec.department_name}
    </div>
  </div>
);

// =============================================================================
// MAIN COMPONENT: OPDConsultationTab
// =============================================================================

const OPDConsultationTab = () => {
  const [tokens,      setTokens]      = useState([]);
  const [selectedToken,  setSelectedToken]  = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showConsultModal, setShowConsultModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [searchQ,  setSearchQ]  = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const pollIntervalRef = useRef(null);

  // ── Department search state (NEW) ─────────────────────────────────────────
  const [searchMode,       setSearchMode]       = useState("queue");  // "queue" | "deptSearch"
  const [deptSearchQuery,  setDeptSearchQuery]  = useState("");
  const [deptSearchResults,setDeptSearchResults]= useState([]);
  const [searchingDept,    setSearchingDept]    = useState(false);
  const deptSearchDebounce = useRef(null);

  // ── Departments for referral (shared with ConsultationModal) ─────────────
  const [departments, setDepartments] = useState([]);

  // ── Load departments once ─────────────────────────────────────────────────
  useEffect(() => {
    departmentAPI.getDepartments()
      .then(data => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // ── Fetch today's token queue (15s poll) ──────────────────────────────────
  const fetchTokens = useCallback(async (silent=false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const data = await receptionAPI.getTokens();
      const list = Array.isArray(data) ? data : (data.results || []);
      setTokens(list);

      // Sync selected token with fresh data
      if (selectedToken) {
        const updated = list.find(t => t.id === selectedToken.id);
        if (updated) {
          setSelectedToken(updated);
          const rec = extractRecord(updated);
          if (rec) setSelectedRecord(rec);
        }
      }
    } catch (e) {
      console.error("OPD queue fetch error:", e);
      if (!silent) setError("Failed to load OPD queue. Ensure the backend is running and you are logged in.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedToken]);

  useEffect(() => {
    fetchTokens();
    // ← 15 s instead of 30 s for more real-time feel
    pollIntervalRef.current = setInterval(() => fetchTokens(true), 15000);
    return () => clearInterval(pollIntervalRef.current);
  }, []); // eslint-disable-line

  // ── Department search handler ─────────────────────────────────────────────
  const runDeptSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setDeptSearchResults([]); return; }
    setSearchingDept(true);
    try {
      const data = await receptionAPI.searchDepartmentPatients({ q });
      setDeptSearchResults(Array.isArray(data) ? data : []);
    } catch { setDeptSearchResults([]); }
    finally  { setSearchingDept(false); }
  }, []);

  const handleDeptSearchChange = (e) => {
    const q = e.target.value;
    setDeptSearchQuery(q);
    if (deptSearchDebounce.current) clearTimeout(deptSearchDebounce.current);
    deptSearchDebounce.current = setTimeout(() => runDeptSearch(q), 350);
  };

  // ── Select a token from queue ─────────────────────────────────────────────
  const handleSelectToken = async (token) => {
    setSelectedToken(token);
    const rec = extractRecord(token);
    if (rec) { setSelectedRecord(rec); return; }
    const recordId = token?.opd_record_id || token?.opd_record?.id || token?.record?.id || token?.record_data?.id;
    if (recordId) {
      try { const fullRecord = await receptionAPI.getOPDRecord(recordId); setSelectedRecord(fullRecord); }
      catch { setSelectedRecord(null); }
    } else { setSelectedRecord(null); }
  };

  // ── Select from department search ─────────────────────────────────────────
  const handleSelectFromDeptSearch = (record) => {
    // Synthesise a minimal token-like object so the panel renders
    setSelectedToken({ id: `deptSearch-${record.id}`, token_number: record.token_number, status: record.status || "WAITING", record_data: record });
    setSelectedRecord(record);
    setSearchMode("queue");
  };

  // ── After clinical save ───────────────────────────────────────────────────
  const handleSaved = (updatedRecord) => {
    setSelectedRecord(updatedRecord);
    fetchTokens(true);
  };

  // ── Filter queue ──────────────────────────────────────────────────────────
  const filteredTokens = tokens.filter((token) => {
    const rec       = extractRecord(token);
    const name      = (rec?.patient_name_snapshot || getPatientName(token) || "").toLowerCase();
    const complaint = (rec?.chief_complaint || token?.chief_complaint || "").toLowerCase();
    const tnum      = String(rec?.token_number || token?.token_number || "");
    // ← FIX: use token.status for filter matching, not record.status
    const status    = token?.status || "";

    const matchSearch = !searchQ || name.includes(searchQ.toLowerCase()) || complaint.includes(searchQ.toLowerCase()) || tnum.includes(searchQ);
    const matchStatus = filterStatus === "ALL" || status === filterStatus;
    return matchSearch && matchStatus;
  });

  // ← FIX: use token.status for counts
  const waitingCount   = tokens.filter(t => t.status === "WAITING").length;
  const completedCount = tokens.filter(t => t.status === "COMPLETED").length;

  const patientId   = selectedRecord?.patient || getPatientId(selectedToken);
  const patientName = selectedRecord?.patient_name_snapshot || getPatientName(selectedToken);

  return (
    <div style={{ display:"flex",height:"100%",minHeight:600,fontFamily:"system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <style>{`@keyframes opd-spin { to { transform: rotate(360deg); } } *:focus { outline: 2px solid #93c5fd; outline-offset: 1px; }`}</style>

      {/* ── LEFT PANEL: Token Queue ── */}
      <div style={{ width:296,flexShrink:0,borderRight:`1px solid ${COLOR.border}`,display:"flex",flexDirection:"column",background:"#fff" }}>

        {/* Queue header */}
        <div style={{ padding:"14px 16px",borderBottom:`1px solid ${COLOR.border}`,background:COLOR.bg,flexShrink:0 }}>
          <div style={{ fontWeight:700,fontSize:14,color:COLOR.text,marginBottom:8 }}>
            Today's OPD Queue
            <span style={{ fontSize:11,fontWeight:400,color:COLOR.gray,marginLeft:8 }}>
              {waitingCount} waiting · {completedCount} done
            </span>
          </div>

          {/* Mode toggle: My Queue ↔ Dept Search */}
          <div style={{ display:"flex",gap:4,marginBottom:8 }}>
            {[["queue","My Queue"],["deptSearch","Dept Search"]].map(([mode,label]) => (
              <button key={mode} onClick={() => setSearchMode(mode)}
                style={{ flex:1,padding:"6px 0",borderRadius:6,border:`1px solid ${searchMode===mode?COLOR.primary:COLOR.border}`,background:searchMode===mode?"#eff6ff":"#fff",color:searchMode===mode?COLOR.primary:COLOR.gray,fontWeight:searchMode===mode?700:400,fontSize:12,cursor:"pointer",transition:"all 0.12s" }}>
                {label}
              </button>
            ))}
          </div>

          {searchMode === "queue" ? (
            <>
              <input style={{ ...styles.input,marginBottom:8 }} placeholder="Search name, complaint, token…" value={searchQ} onChange={e=>setSearchQ(e.target.value)} />
              {/* ← Reactive status filter */}
              <select style={styles.select} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                <option value="ALL">All Statuses ({tokens.length})</option>
                <option value="WAITING">Waiting ({tokens.filter(t=>t.status==="WAITING").length})</option>
                <option value="IN_PROGRESS">In Progress ({tokens.filter(t=>t.status==="IN_PROGRESS").length})</option>
                <option value="COMPLETED">Completed ({tokens.filter(t=>t.status==="COMPLETED").length})</option>
                <option value="CANCELLED">Cancelled ({tokens.filter(t=>t.status==="CANCELLED").length})</option>
              </select>
            </>
          ) : (
            <div>
              <input
                style={{ ...styles.input }}
                placeholder="Search by name, token, phone, Aadhar…"
                value={deptSearchQuery}
                onChange={handleDeptSearchChange}
                autoFocus
              />
              {deptSearchQuery.length > 0 && deptSearchQuery.length < 2 && (
                <div style={{ ...styles.small,marginTop:4 }}>Type at least 2 characters…</div>
              )}
            </div>
          )}
        </div>

        {/* Token / search list */}
        <div style={{ overflowY:"auto",flex:1 }}>
          {searchMode === "queue" ? (
            <>
              {loading && (
                <div style={{ padding:36,textAlign:"center" }}>
                  <LoadingSpinner size={26} />
                  <div style={{ marginTop:10,color:COLOR.lightGray,fontSize:13 }}>Loading queue…</div>
                </div>
              )}
              {error && <div style={{ ...styles.alertError,margin:12,fontSize:12 }}>{error}</div>}
              {!loading && !error && filteredTokens.length === 0 && (
                <div style={{ padding:36,textAlign:"center",color:COLOR.lightGray,fontSize:13 }}>
                  {searchQ || filterStatus !== "ALL" ? "No patients match your search." : "No tokens in queue today."}
                </div>
              )}
              {filteredTokens.map((token, i) => (
                <TokenQueueItem key={token.id || i} token={token} isSelected={selectedToken?.id === token.id} onClick={() => handleSelectToken(token)} />
              ))}
            </>
          ) : (
            <>
              {searchingDept && (
                <div style={{ padding:24,textAlign:"center" }}>
                  <LoadingSpinner size={20} />
                  <div style={{ marginTop:8,color:COLOR.lightGray,fontSize:12 }}>Searching department…</div>
                </div>
              )}
              {!searchingDept && deptSearchQuery.length >= 2 && deptSearchResults.length === 0 && (
                <div style={{ padding:24,textAlign:"center",color:COLOR.lightGray,fontSize:13 }}>No patients found for "{deptSearchQuery}"</div>
              )}
              {!searchingDept && deptSearchQuery.length < 2 && deptSearchQuery.length > 0 && null}
              {deptSearchResults.map((rec, i) => (
                <DeptSearchResultItem
                  key={rec.id || i}
                  record={rec}
                  isSelected={selectedRecord?.id === rec.id}
                  onClick={() => handleSelectFromDeptSearch(rec)}
                />
              ))}
              {!searchingDept && deptSearchQuery.length === 0 && (
                <div style={{ padding:24,textAlign:"center",color:COLOR.lightGray,fontSize:13 }}>
                  <div style={{ fontSize:28,marginBottom:8 }}>🔍</div>
                  Search patients in your department by name, token number, phone or Aadhar
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL: Patient Detail ── */}
        <div style={{ flex:1,background:COLOR.bg,overflow:"hidden" }}>
          <PatientDetailPanel
            token={selectedToken}
            record={selectedRecord}
            onStartConsultation={async () => {
              // Flip WAITING → IN_PROGRESS the moment the doctor opens the consultation
              const tokenId = selectedToken?.id;
              const isDeptSearchToken = typeof tokenId === 'string' && tokenId.startsWith('deptSearch-');
              if (tokenId && !isDeptSearchToken && selectedToken?.status === 'WAITING') {
                try {
                  await receptionAPI.updateTokenStatus(tokenId, 'IN_PROGRESS');
                  setSelectedToken(prev => ({ ...prev, status: 'IN_PROGRESS' }));
                  fetchTokens(true);
                } catch (e) {
                  console.error('Failed to update token status to IN_PROGRESS:', e);
                }
              }
              setShowConsultModal(true);
            }}
            onSkipPatient={async () => {
              const tokenId = selectedToken?.id;
              const isDeptSearchToken = typeof tokenId === 'string' && tokenId.startsWith('deptSearch-');
              if (!tokenId || isDeptSearchToken) return;
              if (!await confirmDialog('Mark this patient as No Show? They will be removed from the active queue.')) return;
              try {
                await receptionAPI.updateTokenStatus(tokenId, 'NO_SHOW');
                setSelectedToken(null);
                setSelectedRecord(null);
                fetchTokens(true);
              } catch (e) {
                console.error('Failed to skip patient:', e);
              }
            }}
            onViewHistory={() => setShowHistoryModal(true)}
          />
        </div>

      {/* ── CONSULTATION MODAL ── */}
      {showConsultModal && selectedToken && (
        <ConsultationModal
          record={selectedRecord}
          token={selectedToken}
          departments={departments}
          onClose={() => setShowConsultModal(false)}
          onSaved={handleSaved}
        />
      )}

      {/* ── HISTORY MODAL ── */}
      {showHistoryModal && (
        <PreviousDetailsModal
          patientId={patientId}
          patientName={patientName}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default OPDConsultationTab;